import React from 'react';
import autoBind from 'react-autobind';
import { If, Then, Else } from 'react-if'
import Switch, { Case } from 'react-switch-case';

// import other components within the same project here
import Config from "../../Config.jsx";

import Localization from "../../Localization.jsx"
import FilePicker from "../FilePicker.jsx"
import ContentMindMap from "./ContentMindMap.jsx"
import ThreeDModelViewer from "./ThreeDModelViewer_Simple.jsx"

import { Textarea, TextInput, Grid, Checkbox, Button, ActionIcon, Text, Tooltip, Box, Group, Divider } from '@mantine/core';
import { Card, Table, Select, SegmentedControl, NumberInput, Progress, Loader, Alert, Popover } from '@mantine/core';
import { IconTrash, IconPlus, IconDownload, IconMicrophone, IconHelp, IconBrandYoutube, IconVideo, IconAlertTriangle, IconInfoCircle, IconCloud } from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../../theme/brandTheme.js";
import ThreeDJobManager, { PHASE } from "../../tools/ThreeDJobManager.js";
import { ENGINES as SelfHostEngines } from "../../tools/SelfHost3D.js";

import YouTube from "react-youtube";
var getYouTubeID = require("get-youtube-id");

var oneMB = 1024 * 1024;
var imageSizeLimit = 10 * oneMB;
var audioSizeLimit = 5 * oneMB;
// Movie clips ship inside the epack, so this file is downloaded onto every
// student device that takes the LOCALE offline. Keep the ceiling deliberately
// low — the same cap is enforced server-side in request_upload, since this
// check is only a client-side courtesy.
var videoSizeLimit = 200 * oneMB;

class ContentDetailView extends React.Component {
  componentDidMount() {
    this._mounted = true;
    // Only load NPC data if this is an NPC content type
    this.loadNPCDataIfNeeded();
    
    // Initialize contentPayload if it's null
    if (!this.state.contentPayload) {
      this.setState({ contentPayload: {} });
    }
    
    // 3D model content: generation runs in ThreeDJobManager (it survives this
    // dialog closing); the view only subscribes for its component's job and
    // verifies an already-generated GLB is reachable before mounting the viewer.
    this.unsubscribe3DJobs = ThreeDJobManager.subscribe(this.handle3DJobUpdate);
    const currentKind = this.state.currentContent && this.state.currentContent.kind != null ? parseInt(this.state.currentContent.kind) : undefined;
    const propKind = this.props.contentType != null ? parseInt(this.props.contentType) : undefined;
    const effectiveKind = (propKind != null ? propKind : currentKind);
    if (effectiveKind === Config.HOTSPOT_CONTENT_3D_MODEL) {
      this.fetchAi3dQuota();
      if (this.state.currentContent && this.state.currentContent.id) {
        this.attach3DModelState();
      }
    }
  }

  // School 3D quota for the badge in the settings card. Server-computed
  // (remaining rows in ai_3d_quotas; total from the subscription privilege).
  // Refreshed after every finished generation, because success is what spends
  // a quota row — on either engine.
  fetchAi3dQuota() {
    // The badge must never just VANISH: an absent badge reads as "this panel
    // has no quota concept", when it actually meant "the lookup never came
    // back" — which is indistinguishable, on screen, from a healthy school.
    // Every exit below therefore records a state the badge can render.
    let settled = false;
    const unknown = (why) => {
      if (settled) return;
      settled = true;
      console.warn('[3D] quota unknown:', why);
      if (this._mounted) this.setState({ ai3dQuota: { known: false, why: why } });
    };
    const schoolId = this.props.root && this.props.root.getMySchoolId ? this.props.root.getMySchoolId() : null;
    if (!schoolId) return unknown('no school id on this account');
    if (!this.props.root.refs || !this.props.root.refs.API) return unknown('API not mounted yet');

    // A request that never answers is the worst case for this panel: no
    // callback fires, so the badge would sit blank forever and read as "this
    // panel has no quota" — which is exactly how a slow /api/ai3d/quota
    // (provisioning thousands of rows) presented. Settle it ourselves.
    const timer = setTimeout(() => unknown('no answer from the quota endpoint after 15s'), 15000);

    this.props.root.refs.API.request("get_ai_3d_quota", { school_id: schoolId }, (res) => {
      clearTimeout(timer);
      if (settled) return;
      settled = true;
      if (!this._mounted) return;
      // Both envelope shapes: the /api surface nests the payload under
      // `result`, the legacy one answered with the object itself.
      const body = res && res.body ? res.body : {};
      const data = (body.result && typeof body.result === 'object') ? body.result : body;
      const enabled = data.AI_3D_model === 1 || data.AI_3D_model === '1';
      const remaining = parseInt(data.AI_3D_quotas || 0, 10) || 0;
      if (!enabled || remaining <= 0) {
        // "No quota" has three very different causes that the UI cannot tell
        // apart (no active school_subscription / privilege 22 off / every
        // ai_3d_quotas row spent), so log what the server actually said —
        // otherwise diagnosing a school that "should have plenty" means
        // guessing. school_id is the one the request was scoped to.
        console.warn('[3D] no generation quota for school', schoolId, '- server said:', data);
      }
      this.setState({
        ai3dQuota: {
          known: true,
          enabled: enabled,
          remaining: remaining,
          // Older servers omit the total; the badge then shows remaining only.
          total: parseInt(data.AI_3D_quotas_total || 0, 10) || 0,
        },
      });
    }, (err) => {
      clearTimeout(timer);
      // Unknown, NOT zero: the badge shows "—" and the start button stays
      // live. The server refuses a generation it cannot fund anyway, so a
      // quota endpoint that is down must not lock the teacher out of the panel.
      unknown((err && err.body && err.body.remarks) ? err.body.remarks : ('lookup failed (HTTP ' + ((err && err.status) || '?') + ')'));
    });
  }

  // Re-attach this component to its generation job (if any) and settle the
  // ready state of an existing model. Safe to call repeatedly.
  //
  // TRUTH LIVES ON THE SERVER: the row this panel opened from can be minutes
  // stale (another browser may have finished, cancelled or imported since the
  // content list was fetched), so we ALWAYS refetch the payload first and
  // derive every lifecycle decision from that. Whether THIS browser can reach
  // the blob host is deliberately not part of the decision — Safari's
  // cross-site policies fail such probes and used to render finished models
  // as \"generating\" forever; the viewer has its own fallback loaders.
  attach3DModelState() {
    const content = this.state.currentContent || {};
    if (!content.id) return;
    this.refresh3DPayloadFromServer((p) => {
      if (p.img_url_3 && p.img_url_3.path) {
        return; // refresh already flipped the view to the ready model
      }

      if (p.gen3d && p.gen3d.cancelled) {
        // Cancelled — stay idle until a new image is uploaded (cancel wipes
        // the old one; nothing may auto-resume here).
        this.setState({ isGenerating3DModel: false, gen3dJob: null });
        return;
      }

      if (p.img_url_1 && p.img_url_1.path) {
        // No model yet but a source image exists. adopt() decides whether that
        // means a job to re-attach to or a component still waiting for the
        // teacher to press 生成3D模型 (payload.gen3d.awaitingStart) — a null
        // job leaves this panel on the form, which is the right place to be.
        const job = ThreeDJobManager.adopt(this.describe3DComponent());
        this.setState({ isGenerating3DModel: Boolean(job), gen3dJob: job || null });
      }
    });
  }

  // Locator bundle the job manager needs to track / reopen this component.
  describe3DComponent() {
    const content = this.state.currentContent || {};
    const parent = this.props.parent && this.props.parent.props && this.props.parent.props.parent;
    const hotspot = parent && parent.props ? parent.props.currentHotspot : null;
    return {
      componentId: content.id,
      hotspotId: hotspot ? hotspot.id : (content.hotspot_id || null),
      epackId: this.props.root.getCurrentLocale() ? this.props.root.getCurrentLocale().id : (content.epack_id || null),
      name: this.state.contentName || content.name || "",
      payload: this.state.contentPayload || {},
    };
  }

  // Manager events for OUR component: mirror the job into view state; on done,
  // pull the fresh payload (img_url_3) from the server and mount the viewer.
  handle3DJobUpdate(job) {
    if (!this._mounted) return;
    const content = this.state.currentContent || {};
    if (!content.id || !job || parseInt(job.componentId) !== parseInt(content.id)) return;

    // Terminal phases hand the panel back to the teacher (retry after a
    // failure, a new upload after a cancel), so the start latch has to go.
    if (job.phase === PHASE.DONE || job.phase === PHASE.FAILED || job.phase === PHASE.CANCELLED) {
      this._starting3D = false;
    }

    if (job.phase === PHASE.DONE) {
      this.setState({ gen3dJob: null, isGenerating3DModel: false });
      this.refresh3DPayloadFromServer();
      this.fetchAi3dQuota(); // success just spent one quota row
    } else if (job.phase === PHASE.CANCELLED) {
      // Mirror the wipe the manager persisted server-side (source image and
      // derivatives removed, tombstone set) so this panel snaps back to the
      // pristine upload state immediately, without a refetch.
      const payload = { ...(this.getNormalizedPayload() || {}) };
      delete payload.img_url_1;
      delete payload.img_url_2;
      delete payload.img_url_4;
      delete payload.self3d;
      payload.gen3d = { ...(payload.gen3d || {}), cancelled: true };
      this.setState({ contentPayload: payload, gen3dJob: null, isGenerating3DModel: false, is3DModelReady: false });
      this.state.contentPayload = payload;
    } else if (job.phase === PHASE.FAILED) {
      this.setState({ gen3dJob: Object.assign({}, job), isGenerating3DModel: false });
    } else {
      this.setState({ gen3dJob: Object.assign({}, job), isGenerating3DModel: true });
    }
  }

  // One-shot payload refresh (replaces the old 5s polling loop, which now
  // lives in ThreeDJobManager for panel-independent progress). `done` receives
  // the fresh payload after state settles. Readiness is PAYLOAD-driven:
  // img_url_3 present = generated — no reachability probe (see
  // attach3DModelState for why); the viewer copes with its own load errors.
  refresh3DPayloadFromServer(done) {
    const currentId = this.state.currentContent && this.state.currentContent.id;
    if (!currentId) {
      if (typeof done === 'function') done(this.state.contentPayload || {});
      return;
    }
    const listReq = { table: "component", filter: { id: String(currentId) }, order_by: "id" };
    this.props.root.refs.API.request("get_list", listReq, (res) => {
      try {
        const list = res && res.body && Array.isArray(res.body.result) ? res.body.result : [];
        if (list.length === 0 || !this._mounted) return;
        const item = list[0] || {};
        const rawPayload = item.payload;
        const latestPayload = typeof rawPayload === 'string'
          ? JSON.parse(rawPayload.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\n\r]/g, ''))
          : (rawPayload || {});
        const hasModelPath = Boolean(latestPayload.img_url_3 && latestPayload.img_url_3.path);
        this.setState({
          contentPayload: latestPayload,
          currentContent: { ...(this.state.currentContent || {}), ...item },
          is3DModelReady: hasModelPath,
          isGenerating3DModel: hasModelPath ? false : this.state.isGenerating3DModel,
        }, () => {
          if (typeof done === 'function') done(latestPayload);
        });
        this.state.contentPayload = latestPayload;
      } catch (e) {
        console.warn('Failed to refresh 3D payload', e);
      }
    }, (err) => {
      console.warn('Failed to refresh 3D payload', err);
    });
  }

  componentWillUnmount() {
    this._mounted = false;
    // Generation itself keeps running in ThreeDJobManager — only the view's
    // subscription ends here.
    if (this.unsubscribe3DJobs) {
      this.unsubscribe3DJobs();
      this.unsubscribe3DJobs = null;
    }
    // Clear the pending transcription timeout so its callback does not run
    // setState / addNotification on an unmounted component.
    if (this.transcribeTimeoutId) {
      clearTimeout(this.transcribeTimeoutId);
    }
  }

  componentDidUpdate(prevProps) {
    // When creating a new component (no currentContent from parent), if contentType changes,
    // reset payload to defaults for the selected kind to avoid leaking previous kind's payload (e.g., NPC).
    if (prevProps.contentType !== this.props.contentType) {
      // Check if we need to load NPC data for the new content type
      this.loadNPCDataIfNeeded();
      
      const parentContentProvider = this.props.parent && this.props.parent.props && this.props.parent.props.parent && this.props.parent.props.parent.getCurrentContent;
      const current = parentContentProvider ? this.props.parent.props.parent.getCurrentContent() : null;
      const isEditingExisting = Boolean(current && current.id);
      if (!isEditingExisting) {
        const kind = this.props.contentType;
        let defaultPayload = {};
        switch (parseInt(kind)) {
          case Config.HOTSPOT_CONTENT_TABLE_AND_CELL:
            defaultPayload = { column_names: ",", row_names: "," };
            break;
          case Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI:
          case Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI:
            defaultPayload = { correct0: false, correct1: false, correct2: false, correct3: false };
            break;
          default:
            defaultPayload = {};
        }
        this.setState({
          currentContent: { kind: kind },
          contentPayload: defaultPayload,
          contentText: "",
          scoreMaxUi: -1,
        });
      }
    }

    // Additionally, if the current kind inside our state changes (e.g., user switches content type within dialog),
    // reset the payload defaults to avoid leaking fields from the previous type (like NPC fields).
    if (prevProps && this.state && prevProps !== undefined) {
      const prevKind = this.prevKindForUpdate;
      const currKind = this.state.currentContent && this.state.currentContent.kind != null ? parseInt(this.state.currentContent.kind) : undefined;
      const isEditingExistingNow = Boolean(this.state.currentContent && this.state.currentContent.id);
      // Only reset defaults when creating new content (no id), not when editing existing content
      // AND only when the kind actually changed (prevKind must be defined and different)
      if (!isEditingExistingNow && prevKind !== undefined && prevKind !== currKind && currKind != null) {
        let defaultPayload = {};
        switch (parseInt(currKind)) {
          case Config.HOTSPOT_CONTENT_TABLE_AND_CELL:
            defaultPayload = { column_names: ",", row_names: "," };
            break;
          case Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI:
          case Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI:
            defaultPayload = { correct0: false, correct1: false, correct2: false, correct3: false };
            break;
          default:
            defaultPayload = {};
        }
        this.setState({ contentPayload: defaultPayload });
      }
      this.prevKindForUpdate = currKind;
    }

    // 3D kind: make sure an in-flight generation is re-attached when the
    // dialog switches to a component that has an image but no model yet.
    try {
      const currentId = this.state.currentContent && this.state.currentContent.id;
      const stateKind = this.state.currentContent && this.state.currentContent.kind != null ? parseInt(this.state.currentContent.kind) : undefined;
      const propKindNow = this.props.contentType != null ? parseInt(this.props.contentType) : undefined;
      const effectiveKindNow = (propKindNow != null ? propKindNow : stateKind);
      const payload = this.state.contentPayload || {};
      const hasModelNow = payload.img_url_3 && payload.img_url_3.path;
      const hasImage = payload.img_url_1 && payload.img_url_1.path;
      const isCancelled = payload.gen3d && payload.gen3d.cancelled;
      const awaitingStart = payload.gen3d && payload.gen3d.awaitingStart;
      if (currentId && effectiveKindNow === Config.HOTSPOT_CONTENT_3D_MODEL && !hasModelNow && hasImage && !isCancelled &&
          !awaitingStart && !this.state.gen3dJob && !ThreeDJobManager.getJob(currentId)) {
        const job = ThreeDJobManager.adopt(this.describe3DComponent());
        if (job) {
          this.setState({ isGenerating3DModel: true, gen3dJob: job });
        }
      }
    } catch (e) {
      // no-op
    }
  }

  loadNPCDataIfNeeded() {
    // Check if we need to load NPC data based on content type
    const contentType = this.props.contentType || (this.state.currentContent && this.state.currentContent.kind);
    const isNPCContent = contentType && parseInt(contentType) === Config.HOTSPOT_CONTENT_NPC;
    
    if (isNPCContent) {
      this.loadNPCData();
    }
  }

  loadNPCData() {
    // Only call these APIs for NPC content types
    // Fetch NPC personalities
    this.props.root.refs.API.request("get_npc_personality_list", {}, this.getNPCPersonalitiesOK, this.getNPCPersonalitiesFailed);
    
    // Fetch NPC avatars for dropdown display
    this.props.root.refs.API.request("get_npc_avatar_list", {}, this.getNPCAvatarsOK, this.getNPCAvatarsFailed);
    
    // Fetch NPC languages from database (with fallback if fails)
    this.props.root.refs.API.request("get_npc_language_list", {}, this.getNPCLanguagesOK, this.getNPCLanguagesFailed);
  }

  getNPCPersonalitiesOK = (res) => {
    let personalities = [];
    if (res && res.body && res.body.result) {
      personalities = res.body.result;
    } else if (res && res.body && Array.isArray(res.body)) {
      personalities = res.body;
    }
    this.setState({ npcPersonalities: personalities });
  }

  getNPCPersonalitiesFailed = (err) => {
    console.log("Failed to load NPC personalities:", err);
    this.setState({ npcPersonalities: [] });
  }

  getNPCAvatarsOK = (res) => {
    let avatars = [];
    if (res && res.body && Array.isArray(res.body.result)) {
      avatars = res.body.result;
    } else if (res && res.body && Array.isArray(res.body)) {
      avatars = res.body;
    }
    avatars = avatars.slice().sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));

    this.setState((prevState) => {
      const currentPayload = prevState.contentPayload || {};
      const hasSelectedAvatar = currentPayload.avatar_id !== undefined && currentPayload.avatar_id !== null && currentPayload.avatar_id !== "";

      if (hasSelectedAvatar || avatars.length === 0) {
        return { npcAvatars: avatars };
      }

      return {
        npcAvatars: avatars,
        contentPayload: {
          ...currentPayload,
          avatar_id: String(avatars[0].id),
        },
      };
    });
  }

  getNPCAvatarsFailed = (err) => {
    console.log("Failed to load NPC avatars:", err);
    this.setState({ npcAvatars: [] }); // Set empty array on failure
  }

  getNPCLanguagesOK = (res) => {
    // Handle different possible response structures with robust error checking
    let languages = [];
    
    try {
      if (res && res.body && res.body.result && Array.isArray(res.body.result)) {
        languages = res.body.result;
      } else if (res && res.body && Array.isArray(res.body)) {
        languages = res.body;
      } else {
        // If response structure is unexpected, use fallback
        throw new Error("Unexpected response structure");
      }
      
      // If we got an empty array or invalid data, use fallback
      if (languages.length === 0) {
        throw new Error("No languages returned");
      }
      
    } catch (error) {
      // Use fallback languages if anything goes wrong
      languages = this.getDefaultLanguages();
    }
    
    this.setState({ npcLanguages: languages });
  }

  getNPCLanguagesFailed = (err) => {
    console.log("Failed to load NPC languages, using fallback:", err);
    // Always provide fallback languages if API fails
    this.setState({ npcLanguages: this.getDefaultLanguages() });
  }

  getDefaultLanguages() {
    return [
      { id: 1, name_en: "Hong Kong (Cantonese)", name_zh: "香港(廣東話)", code: "zh-HK" },
      { id: 2, name_en: "Mandarin", name_zh: "普通話", code: "zh-CN" },
      { id: 3, name_en: "English", name_zh: "English", code: "en" }
    ];
  }

  getContentCategory() {
    return [ Localization.strings.CATEGORY_NONE, Localization.strings.CATEGORY_KNOWLEDGE, Localization.strings.CATEGORY_EXPLORATION, Localization.strings.CATEGORY_EXPERIMENT, Localization.strings.CATEGORY_CREATION, Localization.strings.CATEGORY_EXPERIENCE ];
  }

  static TAG_OK = 0;
  static TAG_WARNING = 1;
  static TAG_ERROR = 2;

  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      currentContent: null, //currentContent.kind <- tag type
      contentName: "",
      contentDifficulty: 1,
      contentText: "",
      correctAnswer: "",
      answerToExclude: "",
      otherGuidelines: "",
      URLText:null,
      VideoId:null,
      contentPayload: null,
      npcPersonalities: [], // List of NPC personality types
      npcAvatars: [], // List of NPC avatars
      npcLanguages: [], // List of NPC languages
      threeDModelProcessing: false, // Track if 3D model is being processed
      lastRefreshTime: null, // Track last refresh time
      isGenerating3DModel: false, // Track if user clicked Generate 3D Model
      transcribing: false, // Track if VOICE OVER audio is being transcribed
      pollingInterval: null, // Store interval ID for polling
      is3DModelReady: false, // Only render the viewer once the GLB is reachable
      moviePlayerExpanded: false, // Movie Clip preview: false = show thumbnail first-frame reference, true = mounted player
      videoTab: "youtube", // Movie Clip source: "youtube" | "upload" (either/or, never both)
    };
    autoBind(this);

    let currentContent = this.props.parent.props.parent.getCurrentContent();
    if(currentContent) {
      this.state.currentContent = currentContent;
      this.state.contentName = currentContent.name;
      this.state.contentDifficulty = parseInt(currentContent.diff);
      this.state.contentText = currentContent.content;
      this.state.URLText = currentContent.URLText;
      this.state.VideoId = currentContent.VideoId;

      // Initialize contentPayload, ensuring it's parsed from string to object if needed
      try {
        this.state.contentPayload = typeof currentContent.payload === 'string' 
          ? JSON.parse(currentContent.payload.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\n\r]/g, '')) 
          : (currentContent.payload || {});
      } catch (error) {
        console.error("Failed to parse payload:", error);
        this.state.contentPayload = {};
      }
      // Movie Clip opens on the tab the clip was saved with. Payloads written
      // before videoSource existed carry only URLText/VideoId, so they open on
      // the YouTube tab — which is what they are.
      let savedPayload = this.state.contentPayload || {};
      if (savedPayload.videoSource == "upload" || (savedPayload.videoSource == null && savedPayload.video0 != null)) {
        this.state.videoTab = "upload";
      }
      // Initialize UI-only score selector strictly from component.score_max; default -1 (不計分)
      const scoreFromComponent = currentContent.score_max;
      let scoreInit = -1;
      if (scoreFromComponent !== undefined && scoreFromComponent !== null && scoreFromComponent !== "") {
        const num = Number(scoreFromComponent);
        scoreInit = Number.isNaN(num) ? -1 : num;
      }
      this.state.scoreMaxUi = scoreInit;
      
      // Additional specialized parsing for question and fill-in-the-blank types has been moved to the general parsing above
    }
    else {
      this.state.currentContent = {};
      this.state.currentContent.kind = this.props.contentType;
      this.state.contentPayload = {};
      this.state.scoreMaxUi = -1; // default UI value (不計分)
      switch (this.state.currentContent.kind) {
        case Config.HOTSPOT_CONTENT_TABLE_AND_CELL:
          this.state.contentPayload.column_names = this.state.contentPayload.row_names = ",";
          break;
        case Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI:
        case Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI:
          this.state.contentPayload.correct0 = this.state.contentPayload.correct1 = this.state.contentPayload.correct2 = this.state.contentPayload.correct3 = false;
          break;
        default:
          break;
      }
    }
  }

  setPayload(payload) {
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  serializePayload() {
    // If payload is already a string, return it
    if (typeof this.state.contentPayload === 'string') {
      try {
        // Ensure NPC fields are not leaked to non-NPC kinds even if payload is string
        const sanitizedStr = this.state.contentPayload.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\n\r]/g, '');
        const obj = JSON.parse(sanitizedStr || '{}');
        const kind = this.state.currentContent && this.state.currentContent.kind != null ? parseInt(this.state.currentContent.kind) : undefined;
        if (kind !== Config.HOTSPOT_CONTENT_NPC) {
          delete obj.npc_name;
          delete obj.avatar_id;
          delete obj.voice_id;
          delete obj.language_id;
          delete obj.persona_id;
          delete obj.npc_info;
          delete obj.student_mission;
          delete obj.time_allowed;
        }
        // Per-clip voice-to-text cache is session-only working state, not content.
        delete obj.transcripts;
        return JSON.stringify(obj);
      } catch (error) {
        console.error("Failed to serialize payload (string):", error);
        return "{}";
      }
    }
    
    // Otherwise, convert the object to a JSON string
    try {
      const payloadCopy = { ...(this.state.contentPayload || {}) };
      const kind = this.state.currentContent && this.state.currentContent.kind != null ? parseInt(this.state.currentContent.kind) : undefined;
      if (kind !== Config.HOTSPOT_CONTENT_NPC) {
        // Remove NPC-specific fields to avoid contaminating other content types
        delete payloadCopy.npc_name;
        delete payloadCopy.avatar_id;
        delete payloadCopy.voice_id;
        delete payloadCopy.language_id;
        delete payloadCopy.persona_id;
        delete payloadCopy.npc_info;
        delete payloadCopy.student_mission;
        delete payloadCopy.time_allowed;
      }
      // Per-clip voice-to-text cache is session-only working state, not content.
      delete payloadCopy.transcripts;
      return JSON.stringify(payloadCopy);
    } catch (error) {
      console.error("Failed to serialize payload:", error);
      return "{}";
    }
  }

  contentNameChanged(e) {
    console.log("contentNameChanged called, new value:", e.target.value);
    this.setState({ contentName: e.target.value });
    this.state.contentName = e.target.value;
    console.log("contentName state updated to:", this.state.contentName);
  }

  contentDifficultyChanged(e) {
    this.setState({ contentDifficulty: e.target.value });
    this.state.contentDifficulty = e.target.value;
  }

  // The image-to-3D panel is vertically tight (source image, params, progress
  // card), so its description shares the title row as a single-line input
  // instead of taking a full-width Textarea card of its own.
  isThreeDModelKind() {
    if (!this.state.currentContent || this.state.currentContent.kind == null) {
      return false;
    }
    return parseInt(this.state.currentContent.kind) === Config.HOTSPOT_CONTENT_3D_MODEL;
  }

  shouldShowScoreSelector() {
    if (!this.state.currentContent || this.state.currentContent.kind == null) {
      return false;
    }
    const kind = parseInt(this.state.currentContent.kind);
    return [
      Config.HOTSPOT_CONTENT_MC_OF_TEXTS,
      Config.HOTSPOT_CONTENT_MC_OF_PICTURES,
      Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI,
      Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI,
      Config.HOTSPOT_CONTENT_QUESTION,
      Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK,
      Config.HOTSPOT_CONTENT_FILL_IN_MIND_MAP,
      Config.HOTSPOT_CONTENT_TAKE_PHOTO,
      Config.HOTSPOT_CONTENT_TABLE_AND_CELL,
      Config.HOTSPOT_CONTENT_TAKE_RECORD,
      Config.HOTSPOT_CONTENT_DRAWING_TASK,
      Config.HOTSPOT_CONTENT_NPC,
    ].includes(kind);
  }

  // The card title and tab strip come from movieClipEditor, so the picker
  // itself is headless. uploadID "video0" is what routes the finished upload
  // into payload.video0 via the generic onUploadSuccess.
  videoPicker(instruction, path="", fileID=""){
    return this.filePicker("", instruction, "mp4", "video", path, videoSizeLimit, "", "video0", fileID, false, null, true);
  }

  imagePicker(title, instruction, extension="jpg,jpeg,png", type="image", path="", sizeLimit=imageSizeLimit, previewPath="", uploadID="", fileID="") {
    return this.filePicker(title, instruction, extension, type, path, sizeLimit, previewPath, uploadID, fileID);
  }

  audioPicker(title, instruction, extension="mp3", type="audio", path="", sizeLimit=audioSizeLimit, previewPath="", uploadID="", fileID="", extraContent=null) {
    // Voice over audio supports recording straight from the microphone in
    // addition to uploading an MP3 file. extraContent (e.g. the voice-to-text
    // section) is rendered inside the same card to form one unified component.
    return this.filePicker(title, instruction, extension, type, path, sizeLimit, previewPath, uploadID, fileID, true, extraContent);
  }

  filePicker(title, instruction, extension, type, path="", sizeLimit=oneMB, previewPath="", uploadID="", fileID="", allowRecording=false, extraContent=null, bare=false) {
    return (
      <FilePicker
        bare={bare}
        root={this.props.root}
        parent={this}
        allowedExtension={extension}
        pickerTitle={title}
        fileType={type}
        fileInstruction={instruction}
        onUploadSuccess={this.onUploadSuccess}
        onDeleteFile={this.onDeleteFile}
        onAbortUpload={this.onAbortUpload}
        filePath={path}
        previewPath={previewPath}
        sizeLimit={sizeLimit}
        uploadID={uploadID}
        fileID={fileID}
        allowRecording={allowRecording}
        onActiveAudioChange={this.setActiveVoiceOverAudio}
        audioFileInfo={allowRecording ? this.getNormalizedPayload().sound0 : null}
        audioSource={allowRecording ? this.getNormalizedPayload().soundSource : ""}
        extraContent={extraContent}
        epackID={this.props.root.getCurrentLocale().id}
      />
    );
  }

  onDeleteFile(fileID){
    switch(parseInt(this.state.currentContent.kind)){
      case Config.HOTSPOT_CONTENT_MC_OF_PICTURES:
        // The picture data lives in contentPayload (what serializePayload persists and
        // what mcPic renders from), so the deletion must clear contentPayload.pictureN,
        // not currentContent.pictureN. Clearing currentContent was silently reverted by
        // mcPic's re-mirror and the deleted image was re-saved.
        if (this.state.contentPayload.picture0 && this.state.contentPayload.picture0.file_id == fileID){
          this.state.contentPayload.picture0 = null;
        }
        if (this.state.contentPayload.picture1 && this.state.contentPayload.picture1.file_id == fileID){
          this.state.contentPayload.picture1 = null;
        }
        if (this.state.contentPayload.picture2 && this.state.contentPayload.picture2.file_id == fileID){
          this.state.contentPayload.picture2 = null;
        }
        if (this.state.contentPayload.picture3 && this.state.contentPayload.picture3.file_id == fileID){
          this.state.contentPayload.picture3 = null;
        }
        this.setState({ contentPayload: { ...this.state.contentPayload } });
        break;
      case Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI:
        if (this.state.contentPayload.picture0 && this.state.contentPayload.picture0.file_id == fileID){
          this.state.contentPayload.picture0 = null;
        }
        if (this.state.contentPayload.picture1 && this.state.contentPayload.picture1.file_id == fileID){
          this.state.contentPayload.picture1 = null;
        }
        if (this.state.contentPayload.picture2 && this.state.contentPayload.picture2.file_id == fileID){
          this.state.contentPayload.picture2 = null;
        }
        if (this.state.contentPayload.picture3 && this.state.contentPayload.picture3.file_id == fileID){
          this.state.contentPayload.picture3 = null;
        }
        break;
      case Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI:
        if (this.state.contentPayload.picture0 && this.state.contentPayload.picture0.file_id == fileID){
          this.state.contentPayload.picture0 = null;
        }
        break;
      case Config.HOTSPOT_CONTENT_MC_OF_TEXTS:
          if (this.state.contentPayload.picture0 && this.state.contentPayload.picture0.file_id == fileID){
            this.state.contentPayload.picture0 = null;
          }
        break;
      case Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK:
        // Only remove picture0, preserve text1, text2, correctAnswer, etc.
        if (this.state.contentPayload.picture0 && this.state.contentPayload.picture0.file_id == fileID){
          this.state.contentPayload.picture0 = null;
        }
        break;
      case Config.HOTSPOT_CONTENT_MOVIECLIP: {
        // Must be explicit: the `default` branch below wipes the whole payload,
        // which for a movie clip would also throw away description/URLText.
        // Clear only the uploaded video, and stay on the Upload tab so the user
        // lands on an empty dropzone rather than being bounced to YouTube.
        let payload = { ...this.getNormalizedPayload() };
        if (payload.video0 && payload.video0.file_id == fileID) {
          delete payload.video0;
        }
        this.setState({ contentPayload: payload });
        this.state.contentPayload = payload;
        // FilePicker only flushes its pending delete when a REPLACEMENT upload
        // lands, so a delete-and-save-nothing would leave the row behind — and
        // that row still carries the epack_id, so every student would keep
        // downloading a video no component references. Delete it now.
        if (fileID) {
          this.props.root.refs.API.request(
            "delete_file",
            { file_id: fileID },
            () => console.log("movie clip video deleted"),
            (err) => console.log("movie clip video delete failed", err),
          );
        }
        break;
      }
      default:
        this.setState({ contentPayload: {} });
        this.state.contentPayload = {};
    }
  }

  onUploadSuccess(res) {
    let fileInfo = {
      ok: res['ok'],
      file_id: res['file_id'],
      display_name: res['display_name'],
      path: res['path'],
      tn_path: res['tn_path'],
      file_type: res['file_type'],
      file_size: res['file_size'],
      message: res['message'],
    };
    let uploadID = res['uploadID'];
    if (uploadID != "") {
      // Create a new object to ensure React detects the change
      let payload = { ...this.state.contentPayload };
      payload[uploadID] = fileInfo;
      if (uploadID == "video0") {
        // Pin the mode alongside the file, so a payload that somehow carries
        // both keys still resolves to the source the author actually chose.
        payload.videoSource = "upload";
        delete payload.URLText;
        delete payload.VideoId;
      }
      this.setState({ contentPayload: payload });
      this.state.contentPayload = payload;

      // For 3D model (kind=16), an uploaded img_url_1 arms the generation — it
      // does NOT start it (see the awaitingStart comment below).
      const currentKind = this.state.currentContent && this.state.currentContent.kind != null
        ? parseInt(this.state.currentContent.kind)
        : (this.props.contentType != null ? parseInt(this.props.contentType) : undefined);

      if (currentKind === Config.HOTSPOT_CONTENT_3D_MODEL && uploadID === 'img_url_1') {
        // A replaced image supersedes any model/job the component had.
        const staleId = this.state.currentContent && this.state.currentContent.id;
        if (staleId) {
          ThreeDJobManager.forget(staleId);
        }
        const cleaned = { ...payload };
        delete cleaned.img_url_3;
        delete cleaned.self3d;
        cleaned.gen3d = { ...(cleaned.gen3d || {}) };
        // A fresh image is fresh intent — a previous cancel no longer applies.
        delete cleaned.gen3d.cancelled;
        // Uploading is not the start gesture: the teacher still has to set the
        // parameters on the right and press 生成3D模型 (generation used to fire
        // from here, leaving no time to choose anything). The marker records
        // "image present, nothing launched" in the payload, so a component
        // saved in this state is not mistaken for a job whose browser died —
        // see ThreeDJobManager.adopt.
        cleaned.gen3d.awaitingStart = true;
        this.setState({ contentPayload: cleaned, is3DModelReady: false, isGenerating3DModel: false, gen3dJob: null });
        this.state.contentPayload = cleaned;
      }
    }
  }

  onAbortUpload() {

  }

  // (The HEAD-probe readiness helpers that lived here were removed on
  // 2026-08-07: lifecycle state now comes from the server payload, and a
  // browser-local reachability check must never decide \"generating vs done\"
  // — Safari's cross-site policies fail those probes and froze finished
  // models on the generating card. ThreeDJobManager keeps one advisory probe
  // for the half-written-upload edge during active watches.)

  // Hand the saved component to the global job manager, which owns the whole
  // pipeline (self-host submit + fallback, or cloud watch) and keeps running
  // after this dialog closes.
  start3DGeneration = () => {
    const p = this.state.contentPayload || {};
    const gen3d = p.gen3d || {};
    const job = ThreeDJobManager.start({
      ...this.describe3DComponent(),
      imageFileInfo: p.img_url_1,
      prompt: gen3d.prompt || "",
      params: gen3d,
    });
    this.setState({ gen3dJob: job, isGenerating3DModel: true, is3DModelReady: false });
  }

  // Cancel button on the progress card. Confirmed first — a misclick would
  // throw away minutes of GPU work.
  handleCancel3DClicked = () => {
    const componentId = this.state.currentContent && this.state.currentContent.id;
    if (!componentId) return;
    const dialogText = {
      title: Localization.strings.GEN3D_CANCEL_CONFIRM_TITLE || 'Cancel 3D generation',
      content: Localization.strings.GEN3D_CANCEL_CONFIRM_BODY || 'Stop generating this 3D model? The uploaded image will be removed too — upload a new image to start again.',
      confirm: Localization.strings.GEN3D_CANCEL || 'Cancel generation',
    };
    this.props.root.refs.confirmDialog.show(dialogText, () => {
      ThreeDJobManager.cancel(componentId);
    }, () => {});
  }

  // (regenerate3DModel was removed with the 已取消生成 card: cancel now resets
  // the component to the pristine upload state, and uploading a new image is
  // the restart gesture — the upload path already clears the cancelled flag.)

  // 刪除 on the viewer card. The model is the component's whole value, so a
  // confirm guards the misclick.
  handleDelete3DModelClicked = () => {
    const strings = Localization.strings;
    const dialogText = {
      title: strings.GEN3D_DELETE_CONFIRM_TITLE || 'Delete 3D model',
      content: strings.GEN3D_DELETE_CONFIRM_BODY || 'Delete this 3D model? The model and its source image will be removed, and you can upload a new image to generate again. Used quota is not refunded.',
      confirm: strings.GEN3D_DELETE_MODEL || 'Delete model',
    };
    this.props.root.refs.confirmDialog.show(dialogText, this.delete3DModel, () => {});
  }

  // Remove every trace of the finished generation and return the panel to its
  // pristine state (empty picker + editable params): payload references,
  // job-manager entry, and the stored files themselves — the GLB and images
  // ride the epack download, so orphans would keep every student downloading
  // a model no component references (same reasoning as the movie-clip delete).
  // gen3d params (prompt/engine/quality/tier) survive: they are the teacher's
  // choices for the NEXT run, not part of the deleted model.
  delete3DModel = () => {
    const componentId = this.state.currentContent && this.state.currentContent.id;
    // Any lingering job entry (e.g. a DONE chip in its grace period) dies with
    // the model.
    if (componentId) ThreeDJobManager.forget(componentId);

    const payload = { ...(this.getNormalizedPayload() || {}) };
    // Collect deletable files BEFORE the references go. img_url_2/4 are
    // server-produced derivatives and may carry no file_id — filtered out.
    const fileIds = ['img_url_1', 'img_url_2', 'img_url_3', 'img_url_4']
      .map((k) => payload[k] && payload[k].file_id)
      .filter(Boolean);
    delete payload.img_url_1;
    delete payload.img_url_2;
    delete payload.img_url_3;
    delete payload.img_url_4;
    delete payload.self3d;
    if (payload.gen3d) {
      // Lifecycle flags belong to the deleted generation, not the next one
      // (the picker is empty again, so there is nothing to await either).
      payload.gen3d = { ...payload.gen3d };
      delete payload.gen3d.cancelled;
      delete payload.gen3d.awaitingStart;
    }

    this.setState({
      contentPayload: payload,
      is3DModelReady: false,
      isGenerating3DModel: false,
      gen3dJob: null,
    });
    this.state.contentPayload = payload;

    if (componentId) {
      // Persist NOW rather than on 更新 — the deleted model must not
      // resurrect when the dialog is closed without saving.
      this.props.root.refs.API.request("update", {
        table: "component",
        where_field: "id",
        where_value: componentId,
        row: { payload: JSON.stringify(payload) },
      }, () => {}, (err) => console.warn("3D delete: payload update failed", err));
    }
    fileIds.forEach((fileId) => {
      this.props.root.refs.API.request(
        "delete_file",
        { file_id: fileId },
        () => {},
        (err) => console.log("3D delete: file cleanup failed", fileId, err),
      );
    });
    if (this.props.root && this.props.root.addNotification) {
      this.props.root.addNotification(Localization.strings.GEN3D_DELETED || '3D model deleted.', true);
    }
  }

  // The 生成3D模型 button. A synchronous latch guards the double click: the
  // generating state only lands via setState (async), and a second click would
  // insert a second component row and spend a second quota.
  handleStart3DClicked = () => {
    if (this._starting3D) return;
    // The quota gate lives HERE, not on the content-type card: opening the
    // panel costs nothing, starting a generation is what spends a row. Only a
    // quota we actually read blocks — an unreachable/undeployed quota endpoint
    // leaves `known` false and the attempt goes through to the server, which
    // does its own check and refuses with a message.
    const quota = this.state.ai3dQuota;
    if (quota && quota.known && (!quota.enabled || quota.remaining <= 0)) {
      this.showNo3DQuota();
      return;
    }
    this.generate3DModel();
  }

  // Two different causes, two different messages: "you have spent them all"
  // (buy more) versus "this school never had the privilege / the subscription
  // is not active" (nothing to buy — an admin has to fix the subscription).
  // The old single string sent schools with an expired subscription off to
  // purchase quota they already owned.
  showNo3DQuota() {
    const quota = this.state.ai3dQuota;
    const message = (quota && quota.enabled === false)
      ? (Localization.strings.GEN3D_NO_PRIVILEGE || '3D model generation is not enabled for this school, or the subscription is not active. Please contact your administrator.')
      : (Localization.strings.NO_3D_QUOTA_CONTACT_ADMIN || 'No 3D model quota available. Please contact admin.');
    const confirmDialog = this.props.root && this.props.root.refs ? this.props.root.refs.confirmDialog : null;
    if (confirmDialog && typeof confirmDialog.show === 'function') {
      confirmDialog.show(
        {
          title: Localization.strings.HOTSPOT_CONTENT_3D_MODEL || '3D Model',
          content: message,
          confirm: Localization.strings.OK || 'OK',
        },
        () => {},
        null,
      );
      return;
    }
    if (this.props.root && typeof this.props.root.addNotification === 'function') {
      this.props.root.addNotification(message, false);
    }
  }

  generate3DModel = () => {
    // Check if image is uploaded
    const p = this.state.contentPayload || {};
    const img1 = p.img_url_1;
    if (!img1 || !img1.path) {
      if (this.props.root && this.props.root.addNotification) {
        this.props.root.addNotification(Localization.strings.NO_IMAGE_UPLOADED_YET || 'Please upload an image on the left side.', false);
      }
      return;
    }

    // Pressing start retires the awaiting-start marker, so the row written
    // below already reads as "a generation was launched" to every other
    // browser (and to adopt() when this panel is reopened).
    const armed = { ...(this.getNormalizedPayload() || {}) };
    if (armed.gen3d && armed.gen3d.awaitingStart) {
      armed.gen3d = { ...armed.gen3d };
      delete armed.gen3d.awaitingStart;
      this.setState({ contentPayload: armed });
      this.state.contentPayload = armed;
    }

    // Set default contentName if empty for 3D model (but don't overwrite if user already typed something)
    let finalContentName = this.state.contentName;
    if (!finalContentName || finalContentName.trim() === "") {
      const defaultName = Localization.strings.MODEL_3D_DEFAULT_NAME || "3D Model";
      finalContentName = defaultName;
      this.setState({ contentName: defaultName });
      this.state.contentName = defaultName;
    }
    
    console.log("generate3DModel called with contentName:", finalContentName);

    // Submit the content without closing dialog (similar to handleFinishClicked in CreateContentDialog)
    let contentDetailViewState = this.state;
    let return_message = this.checkEmptyContent();

    if (return_message.flag === ContentDetailView.TAG_ERROR) {
      this.props.root.addNotification(return_message.message, false);
      return;
    }

    // Past every early return: from here a row is written and a job starts.
    // Latch first (see handleStart3DClicked), then show the generating card
    // right away so the button cannot be pressed twice.
    this._starting3D = true;
    if (!this.state.isGenerating3DModel || this.state.is3DModelReady) {
      this.setState({ isGenerating3DModel: true, is3DModelReady: false });
    }

    // If this is a new content (no id yet), create it
    if (!this.state.currentContent || !this.state.currentContent.id) {
      // Get max display index
      let maxDisplayIndex = -1;
      const parent = this.props.parent.props.parent;
      if (parent && parent.state && parent.state.contentList) {
        for (let i = 0; i < parent.state.contentList.length; i++) {
          let displayIndex = parseInt(parent.state.contentList[i].display_index);
          if (displayIndex > maxDisplayIndex) {
            maxDisplayIndex = displayIndex;
          }
        }
      }
      maxDisplayIndex++;

      let requestObj = {
        table: "component",
        row: {
          name: contentDetailViewState.contentName,
          kind: this.props.contentType,
          display_index: maxDisplayIndex,
          diff: contentDetailViewState.contentDifficulty,
          epack_id: this.props.root.getCurrentLocale().id,
          hotspot_id: parent.props.currentHotspot.id,
          content: contentDetailViewState.contentText,
          payload: this.serializePayload(),
          score_max: (typeof contentDetailViewState.scoreMaxUi === 'number' ? contentDetailViewState.scoreMaxUi : -1),
        }
      };

      this.props.root.refs.API.request("insert", requestObj, (res) => {
        // Set the currentContent with the new id and ensure kind is set
        const insertId = res && res.body && (res.body.insertId || res.body.result);
        if (insertId) {
          const updatedContent = {
            ...this.state.currentContent,
            id: insertId,
            kind: this.props.contentType, // Ensure kind is set
            name: contentDetailViewState.contentName // Store the name used
          };
          this.setState({
            currentContent: updatedContent,
            isGenerating3DModel: true
          }, () => {
            // Component row exists — hand the pipeline to the job manager.
            this.start3DGeneration();
          });
          this.state.currentContent = updatedContent;
          console.log("3D Model component created with ID:", insertId, "Name:", contentDetailViewState.contentName);
        }
        parent.refreshContentList();
        
        if (this.props.root && this.props.root.addNotification) {
          this.props.root.addNotification(Localization.strings.GENERATING_3D_MODEL || 'Generating 3D model...', true);
        }
      }, (err) => {
        console.log("generate 3D model - create content fail", err);
        // Nothing was created: unlatch and drop back to the form so the
        // teacher can press 生成3D模型 again.
        this._starting3D = false;
        this.setState({ isGenerating3DModel: false });
        if (this.props.root && this.props.root.addNotification) {
          this.props.root.addNotification('Failed to create content', false);
        }
      });
    } else {
      // Update existing content
      let requestObj = {
        table: "component",
        where_field: "id",
        where_value: this.state.currentContent.id,
        row: {
          name: contentDetailViewState.contentName,
          diff: contentDetailViewState.contentDifficulty,
          content: contentDetailViewState.contentText,
          payload: this.serializePayload(),
          score_max: (typeof contentDetailViewState.scoreMaxUi === 'number' ? contentDetailViewState.scoreMaxUi : -1),
        }
      };

      this.props.root.refs.API.request("update", requestObj, (res) => {
        this.setState({ isGenerating3DModel: true, is3DModelReady: false });
        // Row updated — hand the pipeline to the job manager.
        this.start3DGeneration();

        if (this.props.root && this.props.root.addNotification) {
          this.props.root.addNotification(Localization.strings.GENERATING_3D_MODEL || 'Generating 3D model...', true);
        }
      }, (err) => {
        console.log("generate 3D model - update content fail", err);
        // The row is unchanged and no job started: unlatch and drop back to
        // the form.
        this._starting3D = false;
        this.setState({ isGenerating3DModel: false });
        if (this.props.root && this.props.root.addNotification) {
          this.props.root.addNotification('Failed to update content', false);
        }
      });
    }
  }

  handle3DModelLoad = (scene) => {
    console.log('Inline 3D Model loaded successfully:', scene);
    this.props.root.addNotification(Localization.strings.MODEL_3D_LOADED_SUCCESS, true);
  }

  handle3DModelError = (error) => {
    console.error('Failed to load inline 3D model:', error);
    this.props.root.addNotification(`${Localization.strings.MODEL_3D_LOAD_FAILED}: ${error.message || 'Unknown error'}`, false);
  }

  // Small (i) icon with a hover/tap explanation, used across the 3D form.
  threeDInfoIcon(info) {
    return (
      <Tooltip label={info} withArrow multiline w={280} position="top" events={{ hover: true, focus: true, touch: true }}>
        <span style={{ display: 'inline-flex', cursor: 'help', verticalAlign: 'middle' }}>
          <IconInfoCircle size={15} stroke={1.8} color={BRAND_PRIMARY} />
        </span>
      </Tooltip>
    );
  }

  threeDFieldLabel(text, info) {
    return (
      <Group gap={4} wrap="nowrap" style={{ display: 'inline-flex' }}>
        <Text size="sm" fw={500} component="span">{text}</Text>
        {info ? this.threeDInfoIcon(info) : null}
      </Group>
    );
  }

  // Generation parameters live in payload.gen3d and are read at submit time —
  // by the self-host submit (prompt) and by the gen-item row (tier/PBR/faces)
  // that the Hunyuan pipeline consumes in cloud mode or on fallback.
  set3DGenParam(key, value) {
    const payload = { ...(this.getNormalizedPayload() || {}) };
    payload.gen3d = { ...(payload.gen3d || {}) };
    payload.gen3d[key] = value;
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  // `disabled` renders the same card read-only: while a job runs the settings
  // cannot change it (they apply to the NEXT run), but hiding them would take
  // away the only record of what this generation was asked to do.
  // `showStart` hides the start button once there is nothing left to start.
  threeDGenParams({ disabled = false, showStart = true } = {}) {
    const p = this.getNormalizedPayload() || {};
    const gen3d = p.gen3d || {};
    const tier = gen3d.tier != null ? String(gen3d.tier) : "1";
    const strings = Localization.strings;
    const hasImage = Boolean(p.img_url_1 && p.img_url_1.path);

    // The knobs face whichever pipeline will actually run. Self mode talks to
    // our GPU platform: it takes a prompt and an engine choice, and ignores
    // the Hunyuan cloud's tier/PBR/face-count knobs entirely. Cloud mode is
    // the reverse. (On a self→cloud fallback the stored defaults apply.)
    const mode = Config.AI_3D_EFFECTIVE_MODE();
    const selectedEngine = SelfHostEngines.find((e) => e.key === gen3d.engine) ||
      SelfHostEngines.find((e) => e.default) || SelfHostEngines[0];

    return (
      <Card withBorder style={{ margin: "0 5px 10px 5px" }}>
        <Group justify="space-between" align="center" mb="sm">
          <Group gap={6}>
            <Text fw={600} component="span">{strings.GEN3D_SETTINGS || '3D generation settings'}</Text>
            {this.threeDInfoIcon(strings.GEN3D_HOW_IT_WORKS || 'Upload a photo of a single object, set the options here and press "Generate 3D Model" — it takes 5-10 minutes. You can close this dialog — generation continues in the background and a small icon at the top right of the map shows its progress.')}
          </Group>
          <Group gap={8} wrap="nowrap">
            {this.renderAi3dQuotaBadge()}
            <Text size="xs" c="dimmed">
              {mode === "self"
                ? (strings.GEN3D_MODE_SELF || 'built-in engine')
                : (strings.GEN3D_MODE_CLOUD || 'cloud service')}
            </Text>
          </Group>
        </Group>

        {disabled && (
          <Text size="xs" c="dimmed" mb="sm">
            {strings.GEN3D_PARAMS_LOCKED || 'Settings are locked while generating — these are the parameters this model is being built with.'}
          </Text>
        )}

        <Textarea
          label={this.threeDFieldLabel(strings.GEN3D_PROMPT_LABEL || 'Material / style hint', strings.GEN3D_PROMPT_INFO || 'Describe the object material and colours (e.g. "glossy ceramic", "matte plastic", "brushed metal"). This improves the generated textures. Optional.')}
          placeholder={strings.GEN3D_PROMPT_PLACEHOLDER || 'e.g. glossy ceramic teapot, dark green'}
          size="sm"
          autosize
          minRows={2}
          maxLength={300}
          disabled={disabled}
          value={gen3d.prompt || ""}
          onChange={(e) => this.set3DGenParam('prompt', e.target.value)}
          mb="sm"
        />

        {mode === "self" && (
          <>
            <Box mb="sm">
              {this.threeDFieldLabel(strings.GEN3D_ENGINE_LABEL || 'Generation engine', strings.GEN3D_ENGINE_INFO || 'Which built-in engine builds the model. Standard: all-round quality with realistic PBR textures (default). Alternative: a second engine — try it if the result is unsatisfactory, or to compare.')}
              <SegmentedControl
                fullWidth
                size="xs"
                mt={4}
                disabled={disabled}
                value={selectedEngine.key}
                onChange={(value) => this.set3DGenParam('engine', value)}
                data={SelfHostEngines.map((e) => ({ label: strings[e.labelKey] || e.labelFallback, value: e.key }))}
              />
            </Box>
            <Box mb="sm">
              {this.threeDFieldLabel(strings.GEN3D_QUALITY_LABEL || 'Model quality', strings.GEN3D_QUALITY_INFO || 'The generated model is always compressed before saving so it loads quickly on student devices. High keeps the most detail; Mid and Low shrink the file further (fewer polygons, smaller textures) for faster loading.')}
              <SegmentedControl
                fullWidth
                size="xs"
                mt={4}
                disabled={disabled}
                value={typeof gen3d.quality === 'string' ? gen3d.quality : 'high'}
                onChange={(value) => this.set3DGenParam('quality', value)}
                data={[
                  { label: strings.GEN3D_QUALITY_HIGH || 'High', value: 'high' },
                  { label: strings.GEN3D_QUALITY_MID || 'Mid', value: 'mid' },
                  { label: strings.GEN3D_QUALITY_LOW || 'Low', value: 'low' },
                ]}
              />
            </Box>
          </>
        )}

        {mode === "cloud" && (
          <>
            <Box mb="sm">
              {this.threeDFieldLabel(strings.GEN3D_TIER_LABEL || 'Generation quality', strings.GEN3D_TIER_INFO || 'Applies to cloud generation (and to the automatic cloud fallback). Standard balances quality and speed; Rapid is faster with simpler results; Pro allows fine control below.')}
              <SegmentedControl
                fullWidth
                size="xs"
                mt={4}
                disabled={disabled}
                value={tier}
                onChange={(value) => this.set3DGenParam('tier', parseInt(value))}
                data={[
                  { label: strings.GEN3D_TIER_STANDARD || 'Standard', value: "1" },
                  { label: strings.GEN3D_TIER_RAPID || 'Rapid', value: "2" },
                  { label: strings.GEN3D_TIER_PRO || 'Pro', value: "3" },
                ]}
              />
            </Box>

            <Group gap={6} mb={tier === "3" ? "sm" : 0}>
              <Checkbox
                size="sm"
                disabled={disabled}
                label={strings.GEN3D_PBR_LABEL || 'PBR materials'}
                checked={gen3d.pbr !== false}
                onChange={(e) => this.set3DGenParam('pbr', e.currentTarget.checked)}
              />
              {this.threeDInfoIcon(strings.GEN3D_PBR_INFO || 'Physically-based rendering textures: richer, more realistic surfaces (metallic / roughness). Recommended on.')}
            </Group>

            {tier === "3" && (
              <Group grow align="flex-start">
                <NumberInput
                  label={this.threeDFieldLabel(strings.GEN3D_FACE_COUNT_LABEL || 'Max faces', strings.GEN3D_FACE_COUNT_INFO || 'Upper bound of the model polygon count (Pro tier only). More faces = more detail but heavier files for student devices.')}
                  size="xs"
                  disabled={disabled}
                  min={10000}
                  max={1000000}
                  step={10000}
                  value={gen3d.faceCount != null ? gen3d.faceCount : 500000}
                  onChange={(value) => this.set3DGenParam('faceCount', typeof value === 'number' ? value : 500000)}
                />
                <Select
                  label={this.threeDFieldLabel(strings.GEN3D_TYPE_LABEL || 'Generation type', strings.GEN3D_TYPE_INFO || 'Normal keeps full detail; LowPoly produces a lighter, stylised model that loads faster on student devices (Pro tier only).')}
                  size="xs"
                  disabled={disabled}
                  value={gen3d.generateType || "Normal"}
                  onChange={(value) => this.set3DGenParam('generateType', value || "Normal")}
                  data={[
                    { value: "Normal", label: strings.GEN3D_TYPE_NORMAL || 'Normal' },
                    { value: "LowPoly", label: strings.GEN3D_TYPE_LOWPOLY || 'LowPoly' },
                  ]}
                />
              </Group>
            )}
          </>
        )}

        {/* The explicit start. Generation used to fire the moment an image
            landed, which left no time to set any of the above — and every run
            spends one of the school's quotas, so it should be a decision. */}
        {showStart && (
          <>
            <Divider my="sm" />
            <Button
              fullWidth
              size="sm"
              color={BRAND_PRIMARY}
              disabled={!hasImage || disabled}
              onClick={this.handleStart3DClicked}
            >
              {strings.GENERATE_3D_MODEL || 'Generate 3D Model'}
            </Button>
            {!hasImage && (
              <Text size="xs" c="dimmed" ta="center" mt={6}>
                {strings.NO_IMAGE_UPLOADED_YET || 'Please upload an image on the left to start generating the 3D model.'}
              </Text>
            )}
          </>
        )}
      </Card>
    );
  }

  // Pill badge: how many 3D models the school can still generate in this
  // subscription. Red at zero.
  renderAi3dQuotaBadge() {
    const quota = this.state.ai3dQuota;
    // Rendered from the first answer onwards, in three states — a number, a
    // red 0, or "—" when the lookup failed. It is only absent for the moment
    // before the request settles: a panel that can spend quota should always
    // say where that quota stands, and "no badge" used to hide both a school
    // with zero and a quota endpoint that never answered.
    if (!quota) return null;
    const strings = Localization.strings;
    const label = strings.REMAINING_QUOTA || 'Remaining quota';

    if (!quota.known) {
      return (
        <Tooltip
          label={(strings.GEN3D_QUOTA_UNKNOWN || 'Remaining quota could not be read; generation will still be attempted.') + (quota.why ? ' (' + quota.why + ')' : '')}
          withArrow multiline w={280} events={{ hover: true, focus: true, touch: true }}
        >
          <Box style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 12, border: '1px solid #adb5bd', cursor: 'help', whiteSpace: 'nowrap' }}>
            <Text size="xs" fw={600} c="dimmed">{label} —</Text>
          </Box>
        </Tooltip>
      );
    }

    const remaining = quota.enabled ? (Number(quota.remaining) || 0) : 0;
    const total = Number(quota.total) || 0;
    const text = total > 0 ? (remaining + " / " + total) : String(remaining);
    const tip = strings.formatString && strings.GEN3D_QUOTA_TOOLTIP
      ? strings.formatString(strings.GEN3D_QUOTA_TOOLTIP, remaining, total > 0 ? total : "—")
      : (label + ": " + text);
    const color = remaining > 0 ? BRAND_PRIMARY : '#e03131';
    return (
      <Tooltip label={tip} withArrow multiline w={280} events={{ hover: true, focus: true, touch: true }}>
        <Box
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 10px',
            borderRadius: 12,
            border: '1px solid ' + color,
            cursor: 'help',
            whiteSpace: 'nowrap',
          }}
        >
          <Text size="xs" fw={600} style={{ color }}>{label} {text}</Text>
        </Box>
      </Tooltip>
    );
  }

  // Human label for the current job phase (see ThreeDJobManager PHASE).
  threeDPhaseLabel(job) {
    const strings = Localization.strings;
    if (!job) return strings.HOTSPOT_CONTENT_3D_MODEL_GENERATING || 'Generating 3D model';
    switch (job.phase) {
      case PHASE.SUBMITTING: return strings.GEN3D_PHASE_SUBMITTING || 'Submitting...';
      case PHASE.QUEUED: return strings.GEN3D_PHASE_QUEUED || 'Waiting for the server...';
      case PHASE.PREPARING: return strings.GEN3D_PHASE_PREPARING || 'Preparing...';
      case PHASE.GENERATING: return strings.GEN3D_PHASE_GENERATING || 'Building the 3D model...';
      case PHASE.FINALIZING: return strings.GEN3D_PHASE_FINALIZING || 'Finishing up...';
      case PHASE.IMPORTING: return strings.GEN3D_PHASE_IMPORTING || 'Saving the model...';
      case PHASE.CLOUD_GENERATING: return strings.GEN3D_PHASE_CLOUD || 'Generating in the cloud...';
      case PHASE.FAILED: return strings.GEN3D_PHASE_FAILED || 'Generation failed';
      default: return strings.HOTSPOT_CONTENT_3D_MODEL_GENERATING || 'Generating 3D model';
    }
  }

  threeDElapsedLabel(job) {
    if (!job || !job.startedAt) return "";
    const sec = Math.max(0, Math.floor((Date.now() - job.startedAt) / 1000));
    if (sec < 60) return sec + "s";
    const min = Math.floor(sec / 60);
    if (min < 60) return min + "m";
    return Math.floor(min / 60) + "h " + (min % 60) + "m";
  }

  threeDModelPreview(previewUrl="") {
    const p = this.state.contentPayload || {};
    const img1 = p.img_url_1; // Uploaded image
    const img2 = p.img_url_2; // Processing image
    const img3 = p.img_url_3; // GLB file
    const img4 = p.img_url_4; // Preview image

    const uploadedImageUrl = img1 && (img1.path || img1.tn_path) ? (Config.UPLOAD_PATH() + (img1.tn_path || img1.path)) : "";
    const processingImageUrl = img2 && (img2.path || img2.tn_path) ? (Config.UPLOAD_PATH() + (img2.tn_path || img2.path)) : "";
    const modelUrl = img3 && img3.path ? (Config.UPLOAD_PATH() + img3.path) : "";
    const previewImageUrl = img4 && (img4.tn_path || img4.path) ? (Config.UPLOAD_PATH() + (img4.tn_path || img4.path)) : "";

    // Show the freshest visual we have as the backdrop, so the teacher sees
    // their photo immediately and server-produced previews as they appear.
    let backgroundImageUrl = "";
    if (previewImageUrl) {
      backgroundImageUrl = previewImageUrl;
    } else if (processingImageUrl) {
      backgroundImageUrl = processingImageUrl;
    } else if (uploadedImageUrl) {
      backgroundImageUrl = uploadedImageUrl;
    }

    const hasUploadedImage = Boolean(img1 && img1.path);
    const isEditing = Boolean(this.state.currentContent && this.state.currentContent.id);
    const job = this.state.gen3dJob;
    const strings = Localization.strings;
    const isCancelled = Boolean(p.gen3d && p.gen3d.cancelled);
    const awaitingStart = Boolean(p.gen3d && p.gen3d.awaitingStart);

    // Nothing uploaded and nothing running: render nothing — the picker title
    // on the left carries the instruction, and the settings card explains the
    // flow. isEditing deliberately plays no part here: an existing component
    // lands in this state right after its model is deleted, and showing the
    // status card then would fake a generation that isn't happening.
    if (!this.state.isGenerating3DModel && !modelUrl && !hasUploadedImage && !job) {
      return null;
    }

    // Cancelled and idle: render nothing — cancel resets the component to the
    // pristine upload state (the manager wipes the source image alongside the
    // job; a component cancelled before that change may still carry its image,
    // which the picker on the left shows for replacement). A new upload IS the
    // restart gesture; the old 已取消生成 card and its 重新生成 button are gone.
    if (isCancelled && !modelUrl && !job) {
      return null;
    }

    // Model generated and reachable: show the viewer.
    if (modelUrl && this.state.is3DModelReady) {
      return (
        <Card withBorder style={{margin:"0 5px 10px 5px"}}>
          <div>
            <div style={{marginBottom: 16}}>
              <Group justify="space-between" align="center" style={{marginBottom: 8}}>
                <Text fw={600} size="sm">
                  {strings.HOTSPOT_CONTENT_3D_MODEL_PREVIEW || '3D Model Preview'}
                </Text>
                <Group gap="xs">
                  <Button
                    variant="default"
                    size="xs"
                    leftSection={<IconDownload size={16} />}
                    onClick={() => {
                      if (modelUrl) window.open(modelUrl, '_blank');
                    }}
                  >
                    {strings.DOWNLOAD_GLB || 'Download GLB'}
                  </Button>
                  <Button
                    variant="light"
                    color="red"
                    size="xs"
                    leftSection={<IconTrash size={16} />}
                    onClick={this.handleDelete3DModelClicked}
                  >
                    {strings.GEN3D_DELETE_MODEL || 'Delete model'}
                  </Button>
                </Group>
              </Group>
              <ThreeDModelViewer
                modelUrl={modelUrl}
                width="100%"
                height="320px"
                fov={45}
                loadingImageUrl={previewImageUrl}
                allowFullscreen
              />
            </div>
          </div>
        </Card>
      );
    }

    // Cancelled and idle is handled above; an existing component whose image
    // is uploaded but not started belongs on the form, not under a spinner —
    // isEditing alone used to be enough to claim "generating" because an image
    // could only mean a launched job.
    if (awaitingStart && !this.state.isGenerating3DModel && !job) {
      return null;
    }

    // A generation is (or should be) running: the status card. Driven by the
    // manager's job when we have one; falls back to a generic generating state
    // (isEditing covers the moment before attach3DModelState re-adopts a job).
    if (this.state.isGenerating3DModel || isEditing || job) {
      const failed = job && job.phase === PHASE.FAILED;
      const progressValue = job && typeof job.progress === 'number' ? job.progress : null;
      const elapsed = this.threeDElapsedLabel(job);

      return (
        <Card withBorder style={{margin:"0 5px 10px 5px"}}>
          <Group justify="space-between" align="center" style={{marginBottom: 8}}>
            <Group gap={8}>
              {!failed && <Loader size="xs" color={BRAND_PRIMARY} />}
              <Text fw={600} size="sm">
                {this.threeDPhaseLabel(job)}
              </Text>
            </Group>
            <Group gap={6}>
              {job && job.fellBack && (
                <Tooltip label={strings.GEN3D_FALLBACK_NOTICE || 'Self-host generation unavailable — switched to cloud generation.'} withArrow multiline w={260}>
                  <span style={{ display: 'inline-flex' }}><IconCloud size={18} color={BRAND_PRIMARY} /></span>
                </Tooltip>
              )}
              {elapsed !== "" && <Text size="xs" c="dimmed">{elapsed}</Text>}
              {!failed && (
                <Button
                  size="compact-xs"
                  variant="light"
                  color="red"
                  onClick={this.handleCancel3DClicked}
                >
                  {strings.GEN3D_CANCEL || 'Cancel generation'}
                </Button>
              )}
            </Group>
          </Group>

          {!failed && (
            <Progress
              value={progressValue != null ? progressValue : 100}
              animated={progressValue == null}
              striped={progressValue == null}
              color={BRAND_PRIMARY}
              size="sm"
              radius="xl"
              mb={8}
            />
          )}

          {job && job.message && (
            <Text size="xs" c="dimmed" mb={8}>{job.message}</Text>
          )}
          {job && job.slow && !failed && (
            <Text size="xs" c="orange" mb={8}>{strings.GEN3D_SLOW_NOTE || 'Taking longer than usual - still working...'}</Text>
          )}

          <Box
            style={{
              width: '100%',
              height: '260px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: backgroundImageUrl ? '#CCC' : '#f5f5f5',
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              position: 'relative',
              overflow: 'hidden',
              marginBottom: 8,
            }}
          >
            {failed && (
              <Button size="xs" color="red" variant="filled" onClick={this.start3DGeneration}>
                {strings.GEN3D_RETRY || 'Retry'}
              </Button>
            )}
          </Box>

          {failed ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} p="xs">
              <Text size="xs">{(job && job.error) || (strings.GEN3D_PHASE_FAILED || 'Generation failed')}</Text>
            </Alert>
          ) : (
            <Alert color="grape" icon={<IconInfoCircle size={16} />} p="xs">
              <Text size="xs">
                {strings.GEN3D_BG_REMINDER || 'You can close this dialog — generation continues in the background. The icon at the top right of the map shows progress and brings you back here.'}
              </Text>
            </Alert>
          )}
        </Card>
      );
    }

    return null;
  }

  contentTextChanged(e) {
    this.setState({ contentText: e.target.value });
    this.state.contentText = e.target.value;
  }

  contentText(title) {
    return (
      <Card withBorder style={{margin:"0 5px 10px 5px"}}>
        <Text fw={600} mb="sm">{title}</Text>
        <div>
          <Textarea
            size="sm"
            value={this.state.contentText}
            onChange={this.contentTextChanged}
            autosize
            minRows={2}
            maxLength={Config.MAX_TEXT_LEN().CONTENT}
          />
        </div>
      </Card>
    );
  }

  // Returns the content payload as an object, parsing it from a string if needed.
  getNormalizedPayload() {
    let payload = this.state.contentPayload;
    if (typeof payload === 'string') {
      try {
        const sanitized = payload.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
        payload = JSON.parse(sanitized);
      } catch (error) {
        console.error('Error parsing content payload in getNormalizedPayload:', error);
        payload = {};
      }
    }
    return payload || {};
  }

  // VOICE OVER: the audio picker exposes two tabs (Upload File / Record) that
  // each keep their own audio. It calls this with the active tab's uploaded
  // file info (or null when that tab is empty); we mirror it into the single
  // payload.sound0 slot so validation, transcription and save keep working off
  // one value and only the currently-active tab is ever saved.
  setActiveVoiceOverAudio(fileInfo, sourceMode) {
    let payload = { ...this.getNormalizedPayload() };
    let newFileId = (fileInfo && fileInfo.file_id) ? fileInfo.file_id : "";

    // Each audio clip keeps its OWN voice-to-text: the Upload File tab's file and
    // the Record tab's recording can both exist within one editing session, each
    // with its own transcript. payload.transcripts maps file_id -> transcript.
    // We stash the transcript currently shown under the clip it belongs to, then
    // restore the newly-active clip's transcript, so toggling tabs shows each
    // clip's text instead of dropping it. payload.transcript stays mirrored to
    // the active clip (that single value is what gets saved).
    let transcripts = { ...(payload.transcripts || {}) };

    // Which clip does the transcript currently in payload.transcript belong to?
    // (Content saved before transcriptFileId existed: it belongs to sound0.)
    let prevOwner = payload.transcriptFileId;
    if (prevOwner == null && payload.transcript && payload.sound0) {
      prevOwner = payload.sound0.file_id;
    }
    if (prevOwner != null && prevOwner !== "") {
      if (typeof payload.transcript === 'string' && payload.transcript !== "") {
        transcripts[prevOwner] = payload.transcript;
      } else {
        delete transcripts[prevOwner];
      }
    }

    if (fileInfo && fileInfo.file_id) {
      payload.sound0 = {
        ok: fileInfo.ok != null ? fileInfo.ok : 1,
        file_id: fileInfo.file_id,
        display_name: fileInfo.display_name,
        path: fileInfo.path,
        tn_path: fileInfo.tn_path,
        file_type: fileInfo.file_type,
        file_size: fileInfo.file_size,
        message: fileInfo.message,
      };
      // Remember which tab produced this audio so re-editing re-opens on the
      // same tab (Record vs Upload File). Anything other than "record" is an
      // upload, which is also the default for legacy content with no source.
      payload.soundSource = sourceMode === "record" ? "record" : "upload";
      // Restore the newly-active clip's transcript (blank if it has none yet).
      payload.transcript = (typeof transcripts[newFileId] === 'string') ? transcripts[newFileId] : "";
      payload.transcriptFileId = newFileId;
      payload.transcripts = transcripts;
    } else {
      delete payload.sound0;
      delete payload.soundSource;
      delete payload.transcript;
      delete payload.transcriptFileId;
      // …and the transcript map too. Leaving it behind kept the payload
      // non-empty, which is exactly what checkEmptyContent tests, so removing
      // the audio no longer raised VOICE_NOT_FILLED and an audio-less voice-over
      // could be saved. serializePayload strips `transcripts` anyway.
      delete payload.transcripts;
    }
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  // ---- VOICE OVER: voice-to-text transcription ----
  transcribeVoiceOver() {
    if (this.state.transcribing) {
      return;
    }

    let payload = this.getNormalizedPayload();
    let sound0 = payload ? payload.sound0 : null;
    if (!sound0 || !sound0.file_id) {
      this.props.root.addNotification(Localization.strings.VOICE_NO_AUDIO, false);
      return;
    }

    this.setState({ transcribing: true });
    this.state.transcribing = true;

    // Transcription downloads the blob and calls the AI service, so guard with a
    // generous client-side timeout in case the request never returns.
    this.transcribeTimeoutId = setTimeout(() => {
      if (this._mounted === false) {
        return;
      }
      if (this.state.transcribing) {
        this.setState({ transcribing: false });
        this.state.transcribing = false;
        this.props.root.addNotification(Localization.strings.TRANSCRIBE_FAILED, false);
      }
    }, 120000);

    let requestObj = { file_id: sound0.file_id, path: sound0.path };
    this.props.root.refs.API.request("get_voice_over_transcription", requestObj, this.transcribeVoiceOverOK, this.transcribeVoiceOverFailed);
  }

  transcribeVoiceOverOK(res) {
    clearTimeout(this.transcribeTimeoutId);
    this.setState({ transcribing: false });
    this.state.transcribing = false;

    let result = res && res.body ? res.body.result : null;
    let text = (result && typeof result.result === 'string') ? result.result : "";
    if (!text) {
      // Surface a backend-provided reason (e.g. an audio/AI error) when present.
      let reason = (result && typeof result.error === 'string') ? result.error : "";
      let message = reason ? (Localization.strings.TRANSCRIBE_FAILED + " (" + reason + ")") : Localization.strings.TRANSCRIBE_FAILED;
      this.props.root.addNotification(message, false);
      return;
    }

    // Store the transcript in the payload so it is persisted (serializePayload)
    // into the component's payload column on save. The user can still edit it.
    // Tag it with the audio it was generated from so toggling tabs back to the
    // same clip keeps it (see setActiveVoiceOverAudio).
    let payload = { ...this.getNormalizedPayload() };
    payload.transcript = text;
    payload.transcriptFileId = (payload.sound0 && payload.sound0.file_id) ? payload.sound0.file_id : "";
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
    this.props.root.addNotification(Localization.strings.TRANSCRIBE_DONE, true);
  }

  transcribeVoiceOverFailed(err) {
    clearTimeout(this.transcribeTimeoutId);
    this.setState({ transcribing: false });
    this.state.transcribing = false;
    console.log("voice over transcription failed", err);
    this.props.root.addNotification(Localization.strings.TRANSCRIBE_FAILED, false);
  }

  voiceOverTranscription() {
    let payload = this.getNormalizedPayload();
    let sound0 = payload ? payload.sound0 : null;
    let hasAudio = !!(sound0 && sound0.file_id);

    // Voice-to-text only applies once a confirmed audio exists, i.e. the user
    // has uploaded an MP3 or submitted a microphone recording (both populate
    // payload.sound0 through the upload pipeline). Hide the whole section until
    // then so it appears only after the audio is in place.
    if (!hasAudio) {
      return null;
    }

    let transcript = (payload && typeof payload.transcript === 'string') ? payload.transcript : "";

    // Returned without an outer Card so it can be embedded inside the
    // "Description of Audio" picker card as one unified component. The top
    // border visually separates the transcription section from the audio area.
    return (
      <div style={{ borderTop: '1px solid #e0e0e0', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <Text fw={600}>{Localization.strings.VOICE_TRANSCRIPTION}</Text>
          <Button
            size="xs"
            onClick={this.transcribeVoiceOver}
            loading={this.state.transcribing}
            leftSection={this.state.transcribing ? undefined : <IconMicrophone size={16} />}
          >
            {this.state.transcribing ? Localization.strings.TRANSCRIBING : Localization.strings.TRANSCRIBE}
          </Button>
        </div>
        <Text size="sm" c="dimmed" style={{ marginBottom: '10px' }}>
          {Localization.strings.VOICE_TRANSCRIPTION_INSTRUCTION}
        </Text>
        <Textarea
          size="sm"
          value={transcript}
          onChange={this.textChanged('transcript')}
          autosize
          minRows={3}
          placeholder=""
          maxLength={5000}
        />
      </div>
    );
  }

  markingScheme() {
    let correctAnswer = "";
    let answerToExclude = "";
    let otherGuidelines = "";

    if (this.state.contentPayload != null) {
      // Handle case where payload might still be a string
      let payload = this.state.contentPayload;
      if (typeof payload === 'string') {
        try {
          const sanitized = payload.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
          payload = JSON.parse(sanitized);
        } catch (error) {
          console.error('Error parsing content payload in markingScheme:', error);
          payload = {};
        }
      }
      
      correctAnswer = (payload.correctAnswer != null ? payload.correctAnswer : "");
      answerToExclude = (payload.answerToExclude != null ? payload.answerToExclude : "");
      otherGuidelines = (payload.otherGuidelines != null ? payload.otherGuidelines : "");
    }

    return (
        <Card withBorder style={{ margin: "0 5px 5px 5px", border: '1px solid #ccc' }}>
            <Text fw={600} mb="sm">{Localization.strings.MARKING_SCHEME}</Text>
            <div style={{ padding: '8px 0' }}>
                <Text size="sm" c="dimmed" style={{ marginBottom: '10px' }}>
                    {Localization.strings.MARKING_SCHEME_INSTRUCTION}
                </Text>

                <Grid gutter="md">
                    <Grid.Col span={6}>
                        <Textarea
                            size="sm"
                            label={Localization.strings.CORRECT_ANSWER}
                            value={correctAnswer}
                            onChange={this.textChanged('correctAnswer')}
                            autosize
                            minRows={3}
                        />
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Textarea
                            size="sm"
                            label={Localization.strings.ANSWER_TO_EXCLUDE}
                            value={answerToExclude}
                            onChange={this.textChanged('answerToExclude')}
                            autosize
                            minRows={3}
                        />
                    </Grid.Col>
                </Grid>
                <Textarea
                    size="sm"
                    label={Localization.strings.OTHER_GUIDELINES}
                    value={otherGuidelines}
                    onChange={this.textChanged('otherGuidelines')}
                    autosize
                    minRows={4}
                    style={{ marginTop: '10px' }}
                />
            </div>
        </Card>
    );
  }

  URLTextChanged(e) {
    this.state.contentPayload.URLText = e.target.value;
    this.state.contentPayload.VideoId = getYouTubeID(e.target.value);
    // Stamp the mode so a clip authored before videoSource existed becomes
    // explicit the moment it is edited, instead of relying on inference.
    this.state.contentPayload.videoSource = "youtube";
    this.state.URLText = e.target.value;
    this.state.VideoId = getYouTubeID(e.target.value);
    // Collapse back to the thumbnail so a newly pasted URL shows its own first frame
    // instead of the previously mounted player.
    this.state.moviePlayerExpanded = false;
    this.setState({ URLText: e.target.value });
    this.setState({ VideoId: getYouTubeID(e.target.value), moviePlayerExpanded: false });
  }
  
  onLoad(e){
    if (this.state.contentPayload != null) {
      console.log("nnnn")
    }
  }

  // MOVIE CLIP: one card, two mutually-exclusive tabs. A clip is EITHER a
  // YouTube URL (streamed, needs a network) OR an uploaded MP4 (rides the epack
  // download and plays offline) — never both, so the app never has to decide
  // which source wins. payload.videoSource records the winner.
  movieClipEditor(title) {
    let isUpload = this.state.videoTab == "upload";
    return (
      <Card withBorder style={{margin:"0 5px 20px 5px"}}>
        <Text fw={600} mb="sm">{title}</Text>
        <div style={{display:'flex', borderBottom:'1px solid #e0e0e0', marginBottom:12}}>
          <Button
            fullWidth
            size="sm"
            variant={!isUpload ? "filled" : "subtle"}
            style={{borderRadius:0}}
            leftSection={<IconBrandYoutube size={16} />}
            onClick={() => this.setMovieClipTab("youtube")}
          >
            {Localization.strings.MOVIECLIP_TAB_YOUTUBE}
          </Button>
          <Button
            fullWidth
            size="sm"
            variant={isUpload ? "filled" : "subtle"}
            style={{borderRadius:0}}
            leftSection={<IconVideo size={16} />}
            onClick={() => this.setMovieClipTab("upload")}
          >
            {Localization.strings.MOVIECLIP_TAB_UPLOAD}
          </Button>
        </div>
        {isUpload ? this.movieClipUploadTab() : this.movieClipYoutubeTab()}
      </Card>
    );
  }

  // Switching tabs discards the other source. The uploaded file must be deleted
  // server-side, not merely dropped from the payload: its `file` row carries the
  // epack_id, so an orphan would stay in the offline download manifest and every
  // student would pull down a video no component references.
  setMovieClipTab(tab) {
    if (this.state.videoTab == tab) {
      return;
    }

    let payload = { ...this.getNormalizedPayload() };

    if (tab == "youtube") {
      let existing = payload.video0;
      if (existing && existing.file_id) {
        let dialogText = {
          title: Localization.strings.MOVIECLIP_SWITCH_TITLE,
          content: Localization.strings.MOVIECLIP_SWITCH_TO_YOUTUBE,
          confirm: Localization.strings.DELETE,
        };
        this.props.root.refs.confirmDialog.show(
          dialogText,
          () => this.confirmMovieClipTab("youtube", existing.file_id),
          () => {},
        );
        return;
      }
      this.confirmMovieClipTab("youtube", null);
      return;
    }

    let hasUrl = (this.state.URLText != null && this.state.URLText != "")
      || (payload.URLText != null && payload.URLText != "");
    if (hasUrl) {
      let dialogText = {
        title: Localization.strings.MOVIECLIP_SWITCH_TITLE,
        content: Localization.strings.MOVIECLIP_SWITCH_TO_UPLOAD,
        confirm: Localization.strings.CONFIRM,
      };
      this.props.root.refs.confirmDialog.show(
        dialogText,
        () => this.confirmMovieClipTab("upload", null),
        () => {},
      );
      return;
    }
    this.confirmMovieClipTab("upload", null);
  }

  confirmMovieClipTab(tab, fileIdToDelete) {
    let payload = { ...this.getNormalizedPayload() };
    payload.videoSource = tab;

    if (tab == "youtube") {
      delete payload.video0;
    } else {
      delete payload.URLText;
      delete payload.VideoId;
    }

    this.setState({
      contentPayload: payload,
      videoTab: tab,
      moviePlayerExpanded: false,
      URLText: tab == "youtube" ? this.state.URLText : null,
      VideoId: tab == "youtube" ? this.state.VideoId : null,
    });
    this.state.contentPayload = payload;
    this.state.videoTab = tab;
    this.state.moviePlayerExpanded = false;
    if (tab != "youtube") {
      this.state.URLText = null;
      this.state.VideoId = null;
    }

    if (fileIdToDelete) {
      this.props.root.refs.API.request(
        "delete_file",
        { file_id: fileIdToDelete },
        () => console.log("movie clip video deleted"),
        (err) => console.log("movie clip video delete failed", err),
      );
    }
  }

  movieClipUploadTab() {
    let payload = this.getNormalizedPayload();
    let video0 = payload.video0;
    let path = (video0 != null && video0.path) ? Config.UPLOAD_PATH() + video0.path : "";
    let fileID = (video0 != null && video0.file_id) ? video0.file_id : "";
    return (
      <div>
        {/* An app that predates MP4 support renders an uploaded clip as
            "unsupported video" and cannot be fixed retroactively, so the
            requirement is stated here rather than discovered in the field.
            Kept visually quiet — it is a caveat, not an error. */}
        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          backgroundColor: '#fff9db',
          border: '1px solid #ffe8a1',
          borderRadius: 6,
          padding: '8px 10px',
          marginBottom: 12,
        }}>
          <IconAlertTriangle size={15} color="#e8590c" style={{flexShrink: 0, marginTop: 2}} />
          <Text size="xs" c="dimmed" style={{lineHeight: 1.6}}>
            {(Localization.strings.MOVIECLIP_APP_VERSION_NOTICE || "").replace(
              "%s",
              Config.MIN_APP_VERSION_VIDEO_UPLOAD,
            )}
          </Text>
        </div>
        {this.videoPicker(
          Localization.strings.MOVIECLIP_UPLOAD_INSTRUCTION,
          path,
          fileID,
        )}
      </div>
    );
  }

  movieClipYoutubeTab(){
    let URLText = "", VideoId = "";
    if (this.state.contentPayload != null) {
      URLText = (this.state.contentPayload.URLText != null? this.state.contentPayload.URLText : "");
      // Derive the id from the URL at render time (mirrors the read-only viewer in
      // LocaleOverviewHotspotContent) so a reopened clip whose saved payload lacks a
      // stored VideoId still previews. Prefer a stored VideoId, else parse the URL.
      VideoId = this.state.contentPayload.VideoId || getYouTubeID(URLText) || "";
    }
    const isWide = window.innerWidth >= 1346;
    // Floor to valid positive dimensions so the iframe can never be 0/NaN-sized
    // (a zero-dimension player also renders as a black box).
    const previewWidth = Math.max(320, isWide ? 600 : Math.round(window.innerWidth * 0.39));
    const playerOpts = {
      height: String(Math.max(180, isWide ? 300 : Math.round(window.innerHeight * 0.5))),
      width: String(previewWidth),
      playerVars: { autoplay: 1 },
    };
    // maxresdefault is crisp when it exists but is missing for many videos. YouTube then
    // either 404s (fires onError) OR serves a 120x90 gray placeholder with HTTP 200 (fires
    // onLoad, no error) — so cover both. Fall back once to hqdefault, which exists as a real
    // frame for every video. The dataset flag prevents a fallback feedback loop.
    const swapToHq = (img) => {
      if (!img.dataset.fb) {
        img.dataset.fb = '1';
        img.src = `https://i.ytimg.com/vi/${VideoId}/hqdefault.jpg`;
      }
    };
    const onThumbError = (e) => swapToHq(e.currentTarget);
    const onThumbLoad = (e) => {
      // A <=120px-wide image is YouTube's "no maxres" placeholder, not the real frame.
      if (e.currentTarget.naturalWidth <= 120) swapToHq(e.currentTarget);
    };
    // The Card and heading come from movieClipEditor, which owns the tab strip.
    return (
      <div>
        <div>
          <Textarea
            size="sm"
            value={URLText}
            onChange={this.URLTextChanged}
            onLoad={this.onLoad}
            autosize
            minRows={1}
          />
        </div>
        {/* Once a valid id is parsed, show the video's first frame as a reference. A static
            YouTube thumbnail loads instantly and works even for videos that block iframe
            embedding; clicking it swaps in the real player (a user gesture, so it can play).
            Guarding on VideoId keeps an empty/invalid URL from rendering a broken image or an
            empty black player. */}
        {VideoId ? (
          <div style={{paddingTop: 12, paddingLeft: isWide ? 0 : window.innerWidth * 0.07}}>
            {this.state.moviePlayerExpanded ? (
              <YouTube videoId={VideoId} opts={playerOpts} onReady={this._onReady}>
              </YouTube>
            ) : (
              <div
                onClick={() => this.setState({ moviePlayerExpanded: true })}
                title={Localization.strings.MOVIECLIP}
                style={{
                  position: 'relative',
                  width: previewWidth,
                  maxWidth: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: 6,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: '#000',
                }}
              >
                <img
                  src={`https://i.ytimg.com/vi/${VideoId}/maxresdefault.jpg`}
                  alt=""
                  onError={onThumbError}
                  onLoad={onThumbLoad}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 68, height: 48, borderRadius: 10,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  <div style={{
                    width: 0, height: 0,
                    borderTop: '11px solid transparent',
                    borderBottom: '11px solid transparent',
                    borderLeft: '18px solid #fff',
                    marginLeft: 4,
                  }} />
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  _onReady(event) {
    // The player is mounted only after an explicit user click (see URLText), so it can
    // play normally. Previously this force-paused an autoplaying player, which left the
    // iframe on a black frame instead of showing the poster/first frame.
  }

  fillInTheBlank() {
    if (parseInt(this.state.currentContent.kind) != Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK) {
        return;
    }

    let text1, text2;
    if (this.state.contentPayload != null) {
      text1 = (this.state.contentPayload.text1 != null? this.state.contentPayload.text1 : "");
      text2 = (this.state.contentPayload.text2 != null? this.state.contentPayload.text2 : "");
    }
    return (
      <div>
        <Card withBorder style={{margin:"0 5px 10px 5px"}}>
          <div>
            <Textarea
              size="sm"
              label={Localization.strings.DESCRIPTION_FILL_IN_THE_BLANK_BEFORE}
              autosize
              minRows={1}
              value={text1}
              onChange={this.textChanged('text1')}
              maxLength={Config.MAX_TEXT_LEN().CONTENT}
            />
            <Textarea
              size="sm"
              label={Localization.strings.DESCRIPTION_FILL_IN_THE_BLANK_AFTER}
              autosize
              minRows={1}
              value={text2}
              onChange={this.textChanged('text2')}
              maxLength={Config.MAX_TEXT_LEN().CONTENT}
              mt="sm"
            />
          </div>
        </Card>
        {this.markingScheme()}
      </div>
    );
  }

  mcText() {
    if (parseInt(this.state.currentContent.kind) != Config.HOTSPOT_CONTENT_MC_OF_TEXTS) {
        return;
    }

    let ans0, ans1, ans2, ans3;
    if (this.state.contentPayload != null) {
      ans0 = (this.state.contentPayload.ans0 != null? this.state.contentPayload.ans0 : "");
      ans1 = (this.state.contentPayload.ans1 != null? this.state.contentPayload.ans1 : "");
      ans2 = (this.state.contentPayload.ans2 != null? this.state.contentPayload.ans2 : "");
      ans3 = (this.state.contentPayload.ans3 != null? this.state.contentPayload.ans3 : "");
    }
    return [
      this.contentText(Localization.strings.DESCRIPTION_QUESTION),
      <Card withBorder style={{margin:"0 5px 10px 5px"}}>
        <div>
          <TextInput
            size="sm"
            label={Localization.strings.CHOICE+" 1 ("+Localization.strings.CORRECT_ANSWER+")"}
            value={ans0}
            onChange={this.textChanged('ans0')}
            maxLength={Config.MAX_TEXT_LEN().CONTENT}
          />
          <TextInput
            size="sm"
            label={Localization.strings.CHOICE+" 2"}
            value={ans1}
            onChange={this.textChanged('ans1')}
            maxLength={Config.MAX_TEXT_LEN().CONTENT}
            mt="sm"
          />
          <TextInput
            size="sm"
            label={Localization.strings.CHOICE+" 3"}
            value={ans2}
            onChange={this.textChanged('ans2')}
            maxLength={Config.MAX_TEXT_LEN().CONTENT}
            mt="sm"
          />
          <TextInput
            size="sm"
            label={Localization.strings.CHOICE+" 4"}
            value={ans3}
            onChange={this.textChanged('ans3')}
            maxLength={Config.MAX_TEXT_LEN().CONTENT}
            mt="sm"
          />
        </div>
      </Card>
    ];
  }

  textChanged = name => event => {
    let payload = this.state.contentPayload;
    
    // Handle the case where payload might be a string
    if (typeof payload === 'string') {
      try {
        // Sanitize only problematic control characters (0-8, 11-12, 14-31)
        // while preserving newlines (9, 10, 13) which are \t, \n, and \r
        const sanitized = payload
          .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
          
        payload = JSON.parse(sanitized);
      } catch (error) {
        console.error('Error parsing content payload in textChanged:', error);
        payload = {}; // Fallback to empty object if parsing fails
      }
    }

    payload[name] = event.target.value;
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  mcTextMulti() {
    if (parseInt(this.state.currentContent.kind) != Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI) {
        return;
    }

    let ans0, ans1, ans2, ans3;
    let correct0, correct1, correct2, correct3;
    if (this.state.contentPayload != null) {
      ans0 = (this.state.contentPayload.ans0 != null? this.state.contentPayload.ans0 : "");
      ans1 = (this.state.contentPayload.ans1 != null? this.state.contentPayload.ans1 : "");
      ans2 = (this.state.contentPayload.ans2 != null? this.state.contentPayload.ans2 : "");
      ans3 = (this.state.contentPayload.ans3 != null? this.state.contentPayload.ans3 : "");
      correct0 = (this.state.contentPayload.correct0 != null? this.state.contentPayload.correct0 : false);
      correct1 = (this.state.contentPayload.correct1 != null? this.state.contentPayload.correct1 : false);
      correct2 = (this.state.contentPayload.correct2 != null? this.state.contentPayload.correct2 : false);
      correct3 = (this.state.contentPayload.correct3 != null? this.state.contentPayload.correct3 : false);
    }
    return [
      this.contentText(Localization.strings.DESCRIPTION_QUESTION),
      <Card withBorder style={{margin:"0 5px 10px 5px"}}>
        <div>
          <Group align="flex-end" gap="sm">
            <TextInput
              size="sm"
              style={{ flex: 1 }}
              label={Localization.strings.CHOICE+" 1"}
              value={ans0}
              onChange={this.textChanged('ans0')}
              maxLength={Config.MAX_TEXT_LEN().CONTENT}
            />
            <Checkbox
              checked={correct0}
              onChange={this.checkboxChanged('correct0')}
              label={Localization.strings.CORRECT}
              mb={6}
            />
          </Group>
          <Group align="flex-end" gap="sm" mt="sm">
            <TextInput
              size="sm"
              style={{ flex: 1 }}
              label={Localization.strings.CHOICE+" 2"}
              value={ans1}
              onChange={this.textChanged('ans1')}
              maxLength={Config.MAX_TEXT_LEN().CONTENT}
            />
            <Checkbox
              checked={correct1}
              onChange={this.checkboxChanged('correct1')}
              label={Localization.strings.CORRECT}
              mb={6}
            />
          </Group>
          <Group align="flex-end" gap="sm" mt="sm">
            <TextInput
              size="sm"
              style={{ flex: 1 }}
              label={Localization.strings.CHOICE+" 3"}
              value={ans2}
              onChange={this.textChanged('ans2')}
              maxLength={Config.MAX_TEXT_LEN().CONTENT}
            />
            <Checkbox
              checked={correct2}
              onChange={this.checkboxChanged('correct2')}
              label={Localization.strings.CORRECT}
              mb={6}
            />
          </Group>
          <Group align="flex-end" gap="sm" mt="sm">
            <TextInput
              size="sm"
              style={{ flex: 1 }}
              label={Localization.strings.CHOICE+" 4"}
              value={ans3}
              onChange={this.textChanged('ans3')}
              maxLength={Config.MAX_TEXT_LEN().CONTENT}
            />
            <Checkbox
              checked={correct3}
              onChange={this.checkboxChanged('correct3')}
              label={Localization.strings.CORRECT}
              mb={6}
            />
          </Group>
        </div>
      </Card>
    ];
  }

  checkboxChanged = name => event => {
    let payload = this.state.contentPayload;
    
    // Handle the case where payload might be a string
    if (typeof payload === 'string') {
      try {
        // Sanitize only problematic control characters (0-8, 11-12, 14-31)
        // while preserving newlines (9, 10, 13) which are \t, \n, and \r
        const sanitized = payload
          .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
          
        payload = JSON.parse(sanitized);
      } catch (error) {
        console.error('Error parsing content payload in checkboxChanged:', error);
        payload = {}; // Fallback to empty object if parsing fails
      }
    }
    
    payload[name] = event.target.checked;
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  mcPic() {
    if (parseInt(this.state.currentContent.kind) != Config.HOTSPOT_CONTENT_MC_OF_PICTURES) {
        return;
    }

    let path0, path1, path2, path3;
    let preview0, preview1, preview2, preview3;
    let file0_ID, file1_ID, file2_ID, file3_ID;

    // Note: contentPayload.pictureN is the single source of truth for MC-of-pictures
    // images (rendered below and persisted by serializePayload). We intentionally do
    // NOT mirror it onto currentContent here — mutating state during render is a React
    // anti-pattern and previously defeated image deletion. Validation reads contentPayload.

    path0 = (this.state.contentPayload.picture0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture0.path : "");
    path1 = (this.state.contentPayload.picture1 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture1.path : "");
    path2 = (this.state.contentPayload.picture2 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture2.path : "");
    path3 = (this.state.contentPayload.picture3 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture3.path : "");
    preview0 = (this.state.contentPayload.picture0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture0.tn_path : "");
    preview1 = (this.state.contentPayload.picture1 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture1.tn_path : "");
    preview2 = (this.state.contentPayload.picture2 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture2.tn_path : "");
    preview3 = (this.state.contentPayload.picture3 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture3.tn_path : "");
    file0_ID = (this.state.contentPayload.picture0 != null? this.state.contentPayload.picture0.file_id : "");
    file1_ID = (this.state.contentPayload.picture1 != null? this.state.contentPayload.picture1.file_id : "");
    file2_ID = (this.state.contentPayload.picture2 != null? this.state.contentPayload.picture2.file_id : "");
    file3_ID = (this.state.contentPayload.picture3 != null? this.state.contentPayload.picture3.file_id : "");
    
    return [
      <Grid.Col span={{ base: 12, sm: 6 }}>
        {this.imagePicker(Localization.strings.CHOICE+" 1 ("+Localization.strings.CORRECT_ANSWER+")", "", "jpg,jpeg,png", "image", path0, imageSizeLimit, preview0, "picture0", file0_ID)}
      </Grid.Col>,
      <Grid.Col span={{ base: 12, sm: 6 }}>
        {this.imagePicker(Localization.strings.CHOICE+" 2", "", "jpg,jpeg,png", "image", path1, imageSizeLimit, preview1, "picture1", file1_ID)}
      </Grid.Col>,
      <Grid.Col span={{ base: 12, sm: 6 }}>
        {this.imagePicker(Localization.strings.CHOICE+" 3", "", "jpg,jpeg,png", "image", path2, imageSizeLimit, preview2, "picture2", file2_ID)}
      </Grid.Col>,
      <Grid.Col span={{ base: 12, sm: 6 }}>
        {this.imagePicker(Localization.strings.CHOICE+" 4", "", "jpg,jpeg,png", "image", path3, imageSizeLimit, preview3, "picture3", file3_ID)}
      </Grid.Col>
    ];
  }

  mcPicMulti() {
    if (parseInt(this.state.currentContent.kind) != Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI) {
        return;
    }

    let correct0, correct1, correct2, correct3;
    if (this.state.contentPayload != null) {
      correct0 = (this.state.contentPayload.correct0 != null? this.state.contentPayload.correct0 : false);
      correct1 = (this.state.contentPayload.correct1 != null? this.state.contentPayload.correct1 : false);
      correct2 = (this.state.contentPayload.correct2 != null? this.state.contentPayload.correct2 : false);
      correct3 = (this.state.contentPayload.correct3 != null? this.state.contentPayload.correct3 : false);
    }

    let path0, path1, path2, path3;
    let preview0, preview1, preview2, preview3;
    let file0_ID, file1_ID, file2_ID, file3_ID;
    path0 = (this.state.contentPayload.picture0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture0.path : "");
    path1 = (this.state.contentPayload.picture1 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture1.path : "");
    path2 = (this.state.contentPayload.picture2 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture2.path : "");
    path3 = (this.state.contentPayload.picture3 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture3.path : "");
    preview0 = (this.state.contentPayload.picture0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture0.tn_path : "");
    preview1 = (this.state.contentPayload.picture1 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture1.tn_path : "");
    preview2 = (this.state.contentPayload.picture2 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture2.tn_path : "");
    preview3 = (this.state.contentPayload.picture3 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture3.tn_path : "");
    file0_ID = (this.state.contentPayload.picture0 != null? this.state.contentPayload.picture0.file_id : "");
    file1_ID = (this.state.contentPayload.picture1 != null? this.state.contentPayload.picture1.file_id : "");
    file2_ID = (this.state.contentPayload.picture2 != null? this.state.contentPayload.picture2.file_id : "");
    file3_ID = (this.state.contentPayload.picture3 != null? this.state.contentPayload.picture3.file_id : "");

    return [
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Checkbox
          checked={correct0}
          onChange={this.checkboxChanged('correct0')}
          label={Localization.strings.CORRECT}
        />
      {this.imagePicker(Localization.strings.CHOICE+" 1", "", "jpg,jpeg,png", "image", path0, imageSizeLimit, preview0, "picture0", file0_ID)}
      </Grid.Col>,
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Checkbox
          checked={correct1}
          onChange={this.checkboxChanged('correct1')}
          label={Localization.strings.CORRECT}
        />
      {this.imagePicker(Localization.strings.CHOICE+" 2", "", "jpg,jpeg,png", "image", path1, imageSizeLimit, preview1, "picture1", file1_ID)}
      </Grid.Col>,
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Checkbox
          checked={correct2}
          onChange={this.checkboxChanged('correct2')}
          label={Localization.strings.CORRECT}
        />
      {this.imagePicker(Localization.strings.CHOICE+" 3", "", "jpg,jpeg,png", "image", path2, imageSizeLimit, preview2, "picture2", file2_ID)}
      </Grid.Col>,
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Checkbox
          checked={correct3}
          onChange={this.checkboxChanged('correct3')}
          label={Localization.strings.CORRECT}
        />
      {this.imagePicker(Localization.strings.CHOICE+" 4", "", "jpg,jpeg,png", "image", path3, imageSizeLimit, preview3, "picture3", file3_ID)}
      </Grid.Col>
    ];
  }

  tableAddColumnClicked() {
    let payload = this.state.contentPayload;
    if (payload.column_names.split(',').length < 6) {
      payload.column_names += ", ";
      this.setState({ contentPayload: payload });
      this.state.contentPayload = payload;
    }
  }

  tableAddRowClicked() {
    let payload = this.state.contentPayload;
    if (payload.row_names.split(',').length < 20) {
      payload.row_names += ", ";
      this.setState({ contentPayload: payload });
      this.state.contentPayload = payload;
    }
  }

  tableDeleteColumnClicked(index) {
    let column_names = "";
    let payload = this.state.contentPayload;
    let columns = payload.column_names.split(',');
    if (columns.length > 2) {
      for (let i = 0; i < columns.length; i++) {
        if (i != index) {
          column_names += ("," + columns[i]);
        }
      }
      column_names = column_names.substring(1);
      payload.column_names = column_names;
      this.setState({ contentPayload: payload });
      this.state.contentPayload = payload;
    }
  }

  tableDeleteRowClicked(index) {
    let row_names = "";
    let payload = this.state.contentPayload;
    let rows = payload.row_names.split(',');
    if (rows.length > 2) {
      for (let i = 0; i < rows.length; i++) {
        if (i != index) {
          row_names += ("," + rows[i]);
        }
      }
      row_names = row_names.substring(1);
      payload.row_names = row_names;
      this.setState({ contentPayload: payload });
      this.state.contentPayload = payload;
    }
  }

  tableColumnChanged = index => event => {
    console.log(event.target.value);
    let column_names = "";
    let payload = this.state.contentPayload;
    let columns = payload.column_names.split(',');
    for (let i = 0; i < columns.length; i++) {
      if (i != index) {
        column_names += ("," + columns[i]);
      }
      else {
        column_names += ("," + event.target.value.replace(/,/g, ''));
      }
    }
    column_names = column_names.substring(1);
    payload.column_names = column_names;
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  tableRowChanged = index => event => {
    let row_names = "";
    let payload = this.state.contentPayload;
    let rows = payload.row_names.split(',');
    for (let i = 0; i < rows.length; i++) {
      if (i != index) {
        row_names += ("," + rows[i]);
      }
      else {
        row_names += ("," + event.target.value.replace(/,/g, ''));
      }
    }
    row_names = row_names.substring(1);
    payload.row_names = row_names;
    this.setState({ contentPayload: payload });
    this.state.contentPayload = payload;
  }

  customTable() {
    if (parseInt(this.state.currentContent.kind) != Config.HOTSPOT_CONTENT_TABLE_AND_CELL) {
        return;
    }

    let columns=[], rows=[];
    if (this.state.contentPayload != null) {
      columns = (this.state.contentPayload.column_names != null? this.state.contentPayload.column_names.split(',') : []);
      rows = (this.state.contentPayload.row_names != null? this.state.contentPayload.row_names.split(',') : []);
    }
    return (
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th></Table.Th>
            {
              columns.map( (column, i) => {
                return (
                  <Table.Th key={i}>
                    <TextInput
                      label={i+1}
                      value={column}
                      onChange={this.tableColumnChanged(i)}
                      rightSection={<ActionIcon variant="subtle" color="red" onClick={() => this.tableDeleteColumnClicked(i)} disabled={columns.length <= 2}><IconTrash size={18} /></ActionIcon>}
                      maxLength={Config.MAX_TEXT_LEN().NAME}
                    />
                  </Table.Th>
                )
              })
            }
            <Table.Th>
              <Button
                onClick={this.tableAddColumnClicked}
                disabled={columns.length >= 6}
                leftSection={<IconPlus size={16} />}
              >
                {Localization.strings.COLUMN}
              </Button>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {
            rows.map( (row, i) => {
              return (
                <Table.Tr key={i}>
                  <Table.Td>
                    <TextInput
                      label={i+1}
                      value={row}
                      onChange={this.tableRowChanged(i)}
                      rightSection={<ActionIcon variant="subtle" color="red" onClick={() => this.tableDeleteRowClicked(i)} disabled={rows.length <= 2}><IconTrash size={18} /></ActionIcon>}
                      maxLength={Config.MAX_TEXT_LEN().NAME}
                    />
                  </Table.Td>
                  {
                    columns.map( (dummy, j) => {
                      return (
                        <Table.Td key={j}/>
                      )
                    })
                  }
                </Table.Tr>
              )
            })
          }
          <Table.Tr>
            <Table.Td>
              <Button
                onClick={this.tableAddRowClicked}
                disabled={rows.length >= 20}
                leftSection={<IconPlus size={16} />}
              >
                {Localization.strings.ROW}
              </Button>
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    );
  }

  checkEmptyContent(){
    let return_message = {
      //flag: 0: ok, 1: warning, 2: error
      flag: ContentDetailView.TAG_OK,
      message: ""
    }
    if (this.state.currentContent.kind){
      switch(parseInt(this.state.currentContent.kind)){
        case Config.HOTSPOT_CONTENT_SIMPLE_DESCRIPTION:
          if (this.state.contentText == "" && Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_WARNING;
            return_message.message = Localization.strings.DESCRIPTION_NOT_FILLED;
          }else if (Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_WARNING;
            return_message.message = Localization.strings.IMAGE_NOT_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_VOICE_OVER: {
          // Test the field that actually holds the audio, not "is the payload
          // object empty" — bookkeeping keys (transcripts, soundSource) made an
          // audio-less payload look filled.
          const hasAudio = !!(this.state.contentPayload && this.state.contentPayload.sound0);
          if (this.state.contentText == "" && !hasAudio){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else if (!hasAudio){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.VOICE_NOT_FILLED;
          }else if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_WARNING;
            return_message.message = Localization.strings.DESCRIPTION_NOT_FILLED;
          }
          break;
        }

        case Config.HOTSPOT_CONTENT_MOVIECLIP: {
          // A clip is EITHER an uploaded MP4 or a YouTube URL, so the two modes
          // are validated separately: requiring a URL on the Upload tab used to
          // make an uploaded-only clip unsaveable (EMPTY_URL).
          let clipPayload = this.getNormalizedPayload();
          if (this.state.contentText == "" && Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
            break;
          }
          if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.DESCRIPTION_NOT_FILLED;
            break;
          }

          if (this.state.videoTab == "upload") {
            if (clipPayload.video0 == null || !clipPayload.video0.path) {
              return_message.flag = ContentDetailView.TAG_ERROR;
              return_message.message = Localization.strings.VIDEO_NOT_UPLOADED;
            }
            break;
          }

          if(this.state.URLText == undefined){
            // do nothing
          }
          else if ((this.state.URLText == null || this.state.URLText == "") && (clipPayload.URLText ==null || clipPayload.URLText =="")) {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.EMPTY_URL;
          }
          // youtu.be short links are valid YouTube URLs and getYouTubeID parses
          // them, but the old "www.youtube.com/watch" substring test rejected
          // them outright. Trust the parsed id instead, which is what both the
          // preview and the app actually use.
          else if (this.state.URLText != null && this.state.VideoId == null ){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.YOUTUBE_ID_INVALID;
          }
          break;
        }

        case Config.HOTSPOT_CONTENT_MC_OF_TEXTS:
          if (this.state.contentText == "" && Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.QUESTION_NOT_FILLED;
          }else if (!this.state.contentPayload.ans0 || this.state.contentPayload.ans0 == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.CORRECT_ANSWER_CONTECT_NOT_FILLED;
          }else if (!this.state.contentPayload.picture0){
            return_message.flag = ContentDetailView.TAG_WARNING;
            return_message.message = Localization.strings.IMAGE_NOT_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_MC_OF_PICTURES:
          if (this.state.contentText == "" && Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.QUESTION_NOT_FILLED;
          }else if (!this.state.contentPayload.picture0 || !this.state.contentPayload.picture1 || !this.state.contentPayload.picture2 || !this.state.contentPayload.picture3){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.ANSWER_NOT_ALL_FILLED;
          }
          break;
          
        case Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI:
          if (this.state.contentText == "" && !this.state.contentPayload.ans0 && !this.state.contentPayload.ans1 && !this.state.contentPayload.ans2 && !this.state.contentPayload.ans3){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.QUESTION_NOT_FILLED;
          }else if (
            (this.state.contentPayload.correct0 == true && (!this.state.contentPayload.ans0 || this.state.contentPayload.ans0 == "")) ||
            (this.state.contentPayload.correct1 == true && (!this.state.contentPayload.ans1 || this.state.contentPayload.ans1 == "")) ||
            (this.state.contentPayload.correct2 == true && (!this.state.contentPayload.ans2 || this.state.contentPayload.ans2 == "")) ||
            (this.state.contentPayload.correct3 == true && (!this.state.contentPayload.ans3 || this.state.contentPayload.ans3 == ""))
          ){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.CORRECT_ANSWER_CONTECT_NOT_FILLED;
          }else if(this.state.contentPayload.correct0 == false && this.state.contentPayload.correct1 == false && this.state.contentPayload.correct2 == false && this.state.contentPayload.correct3 == false){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.CORRECT_ANSWER_NOT_SELECTED;
          }else if (!this.state.contentPayload.picture0){
            return_message.flag = ContentDetailView.TAG_WARNING;
            return_message.message = Localization.strings.IMAGE_NOT_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI:
          if (this.state.contentText == "" && !this.state.contentPayload.picture0 && !this.state.contentPayload.picture1 && !this.state.contentPayload.picture2 && !this.state.contentPayload.picture3){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.QUESTION_NOT_FILLED;
          }else if (
            (this.state.contentPayload.correct0 == true && (!this.state.contentPayload.picture0)) ||
            (this.state.contentPayload.correct1 == true && (!this.state.contentPayload.picture1)) ||
            (this.state.contentPayload.correct2 == true && (!this.state.contentPayload.picture2)) ||
            (this.state.contentPayload.correct3 == true && (!this.state.contentPayload.picture3))
          ){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.CORRECT_ANSWER_CONTECT_NOT_FILLED;
          }else if(this.state.contentPayload.correct0 == false && this.state.contentPayload.correct1 == false && this.state.contentPayload.correct2 == false && this.state.contentPayload.correct3 == false){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.CORRECT_ANSWER_NOT_SELECTED;
          }else if (!this.state.contentPayload.picture0 || !this.state.contentPayload.picture1 || !this.state.contentPayload.picture2 || !this.state.contentPayload.picture3){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.ANSWER_NOT_ALL_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_QUESTION:
          // The marking scheme is OPTIONAL, exactly as it is for the other two kinds
          // that render it (9 fill-in-the-blank, 13 take-record). Every backend gate
          // treats correctAnswer / answerToExclude / otherGuidelines as a 3-way OR, so
          // requiring correctAnswer here blocked a question graded purely by "Other
          // Guidelines", and blocked a question meant to carry no AI grading at all.
          if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.QUESTION_NOT_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK:
          if ((this.state.contentPayload.text1 == "" && this.state.contentPayload.text2 == "") || Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.FILL_AT_LEAST_ONE;
          }else if (!this.state.contentPayload.picture0){
            return_message.flag = ContentDetailView.TAG_WARNING;
            return_message.message = Localization.strings.IMAGE_NOT_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_FILL_IN_MIND_MAP:
          if (Object.keys(this.state.contentPayload).length == 0){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }else {
            let links = this.state.contentPayload.linkList || [];
            let boxes = this.state.contentPayload.boxList || [];
            if (links.length == 0 && boxes.length == 0){
              return_message.flag = ContentDetailView.TAG_ERROR;
              return_message.message = Localization.strings.NTH_FILLED;
            }
          }
          break;

        case Config.HOTSPOT_CONTENT_TAKE_PHOTO:
          if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_TABLE_AND_CELL:
          let row_OK = !(this.state.contentPayload.row_names.match(/^,/g) || this.state.contentPayload.row_names.match(/,$/g) || this.state.contentPayload.row_names.match(/,,/g));
          let col_OK = !(this.state.contentPayload.column_names.match(/^,/g) || this.state.contentPayload.column_names.match(/,$/g) || this.state.contentPayload.column_names.match(/,,/g));

          if (!row_OK || !col_OK){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.ROW_COL_NOT_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_TAKE_RECORD:
          if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_DRAWING_TASK:
          if (this.state.contentText == ""){
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NTH_FILLED;
          }
          break;

        case Config.HOTSPOT_CONTENT_3D_MODEL:
          // For 3D model, no validation needed
          // Content name will default to "3D模型"/"3D Model" if empty
          // Text description can be empty (default null)
          break;

        case Config.HOTSPOT_CONTENT_NPC:
          const npcData = this.state.contentPayload || {};
          if (!npcData.npc_name || npcData.npc_name.trim() === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NPC_NAME_NOT_FILLED;
          }
          else if (!npcData.avatar_id || npcData.avatar_id === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NPC_AVATAR_NOT_FILLED; 
          }
          else if (!npcData.voice_id || npcData.voice_id === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
              return_message.message = Localization.strings.NPC_VOICE_GENDER_NOT_FILLED;
          }
          else if (!npcData.language_id || npcData.language_id === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NPC_LANGUAGE_NOT_FILLED;
          }
          else if (!npcData.persona_id || npcData.persona_id === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NPC_PERSONALITY_NOT_FILLED;
          }
          else if (!npcData.npc_info || npcData.npc_info.trim() === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NPC_BACKGROUND_INFO_NOT_FILLED;
          }
          else if (!npcData.student_mission || npcData.student_mission.trim() === "") {
            return_message.flag = ContentDetailView.TAG_ERROR;
            return_message.message = Localization.strings.NPC_STUDENT_MISSION_NOT_FILLED;
          }
          break;
      }
    }
    return return_message;
  }

  handleSuggestQuestionClick() {
    // Retrieve values from state
    const { contentText, contentDifficulty } = this.state;
  
    // Log the details to the console
    console.log('Suggest Question Clicked with details:');
    console.log('Content Text:', contentText);
    console.log('Difficulty:', contentDifficulty);
  
    // Placeholder for the future API call
    console.log('TODO: Implement API call here with the above details.');
  }
  npcForm() {
    
    // Initialize NPC data from payload
    let npcData = this.state.contentPayload || {};
    
    // MIGRATION: If contentPayload is empty but contentText has data, 
    // this might be old NPC content stored as text (npc_name in contentText)
    if ((!npcData || Object.keys(npcData).length === 0) && this.state.contentText) {
      npcData = {
        npc_name: this.state.contentText,
        voice_id: "",
        language_id: "",
        time_allowed: "",
        avatar_id: "",
        persona_id: "",
        npc_info: "",
        student_mission: ""
      };
      
      // Update the state with migrated data
      this.setState({ contentPayload: npcData });
    }
    
    const npcName = npcData.npc_name || "";
    const avatarId = npcData.avatar_id || (this.state.npcAvatars[0] ? String(this.state.npcAvatars[0].id) : "");
    const voiceId = npcData.voice_id || ""; // 1=Male, 2=Female  
    const npcLanguage = npcData.language_id || ""; //  1=Cantonese, 2=Mandarin, 3=English
    const timeAllowed = npcData.time_allowed || ""; // Default 5 minutes
    const personaId = npcData.persona_id || "";
    const npcInfo = npcData.npc_info || "";
    const studentMission = npcData.student_mission || "";



    return [
      // NPC Basic Configuration
      <Card withBorder key="npc-basic-config" style={{margin:"0 5px 10px 5px"}}>
        <Text fw={600} mb="sm">{Localization.strings.NPC_AVATAR + " - " + Localization.strings.NPC_BASIC_CONFIG}</Text>
        <div>
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                size="sm"
                label={Localization.strings.NPC_NAME}
                value={npcName}
                onChange={this.npcFieldChanged('npc_name')}
                maxLength={100}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <div style={{marginTop: "16px"}}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#24292f',
                  marginBottom: '8px'
                }}>
                  {Localization.strings.NPC_AVATAR_SELECT}
                </div>
                {this.renderGitHubStyleAvatarSelector()}
              </div>
            </Grid.Col>
          </Grid>
        </div>
      </Card>,

      // NPC Voice & Language Settings
      <Card withBorder key="npc-voice-language" style={{margin:"0 5px 10px 5px"}}>
        <Text fw={600} mb="sm">{Localization.strings.NPC_VOICE_LANGUAGE_CONFIG}</Text>
        <div>
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <div style={{position: 'relative'}}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {Localization.strings.NPC_VOICE_GENDER}
                </label>
                <select
                  value={voiceId}
                  onChange={this.npcFieldChanged('voice_id')}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '8px 32px 8px 14px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1976d2';
                    e.target.style.borderWidth = '2px';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                    e.target.style.borderWidth = '1px';
                  }}
                >
                  <option value="" disabled>
                    {Localization.strings.PLEASE_SELECT}
                  </option>
                  <option value="1">{Localization.strings.VOICE_MALE}</option>
                  <option value="2">{Localization.strings.VOICE_FEMALE}</option>
                </select>
              </div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <div style={{position: 'relative'}}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {Localization.strings.NPC_LANGUAGE}
                </label>
                <select
                  value={npcLanguage}
                  onChange={this.npcFieldChanged('language_id')}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '8px 32px 8px 14px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1976d2';
                    e.target.style.borderWidth = '2px';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                    e.target.style.borderWidth = '1px';
                  }}
                >
                  <option value="" disabled>
                    {Localization.strings.PLEASE_SELECT}
                  </option>
                  {this.renderLanguageOptions()}
                </select>
              </div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <div style={{position: 'relative'}}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {Localization.strings.NPC_TIME_ALLOWED}
                </label>
                <select
                  value={timeAllowed}
                  onChange={this.npcFieldChanged('time_allowed')}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '8px 32px 8px 14px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1976d2';
                    e.target.style.borderWidth = '2px';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                    e.target.style.borderWidth = '1px';
                  }}
                >
                  <option value="" disabled>
                    {Localization.strings.PLEASE_SELECT}
                  </option>
                  <option value="2">{Localization.strings.TIME_2_MIN}</option>
                  <option value="5">{Localization.strings.TIME_5_MIN}</option>
                  <option value="10">{Localization.strings.TIME_10_MIN}</option>
                </select>
              </div>
            </Grid.Col>
          </Grid>
        </div>
      </Card>,

      // NPC Personality
      <Card withBorder key="npc-personality" style={{margin:"0 5px 10px 5px"}}>
        <Text fw={600} mb="sm">{Localization.strings.NPC_PERSONALITY_CONFIG}</Text>
        <div>
          <div style={{position: 'relative'}}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              color: '#666',
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              {Localization.strings.NPC_PERSONALITY}
            </label>
            <select
              value={personaId}
              onChange={this.npcFieldChanged('persona_id')}
              style={{
                width: '100%',
                height: '40px',
                padding: '8px 32px 8px 14px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                fontSize: '16px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                outline: 'none',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
                e.target.style.borderWidth = '2px';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                e.target.style.borderWidth = '1px';
              }}
            >
              <option value="" disabled>
                {Localization.strings.PLEASE_SELECT}
              </option>
              {this.renderPersonalityOptions()}
            </select>
          </div>
        </div>
      </Card>,

      // NPC Background Information
      <Card withBorder key="npc-background" style={{margin:"0 5px 10px 5px"}}>
        <Text fw={600} mb="sm">{Localization.strings.NPC_BACKGROUND_INFO}</Text>
        <div>
          <Textarea
            size="sm"
            autosize
            minRows={6}
            value={npcInfo}
            onChange={this.npcFieldChanged('npc_info')}
            maxLength={5000}
            required
            description={`${npcInfo.length}/5000 ${Localization.strings.CHARACTERS}`}
          />
        </div>
      </Card>,

      // Student Mission / Marking Scheme
      <Card withBorder key="npc-mission" style={{margin:"0 5px 10px 5px"}}>
        <Text fw={600} mb="sm">{Localization.strings.NPC_STUDENT_MISSION}</Text>
        <div>
          <Textarea
            size="sm"
            autosize
            minRows={6}
            value={studentMission}
            onChange={this.npcFieldChanged('student_mission')}
            maxLength={5000}
            required
            description={`${studentMission.length}/5000 ${Localization.strings.CHARACTERS} - ${Localization.strings.REQUIRED}`}
          />
        </div>
      </Card>
    ];
  }

  npcFieldChanged = (fieldName) => (event) => {
    let value = event.target.value;
    // Coerce numeric fields to numbers
    if (['language_id', 'avatar_id', 'persona_id', 'voice_id', 'time_allowed'].includes(fieldName)) {
      if (value === "") {
        // keep empty as empty for UI placeholder
      } else {
        const num = Number(value);
        if (!Number.isNaN(num)) {
          value = num;
        }
      }
    }
    
    let newPayload = { ...this.state.contentPayload };
    if (!newPayload) {
      newPayload = {};
    }
    newPayload[fieldName] = value;
    
    this.setState({ contentPayload: newPayload });
  }

  renderPersonalityOptions() {
    const { npcPersonalities } = this.state;
    const language = Localization.strings.getLanguage();
    
    if (!npcPersonalities || npcPersonalities.length === 0) {
      return (
        <option value="" disabled>No personalities available</option>
      );
    }
    
    return npcPersonalities.map(personality => {
      const name = language === "en" ? personality.name_en : personality.name_zh;
      const description = language === "en" ? personality.desc_en : personality.desc_zh;
      const displayText = description ? `${name} - ${description}` : name;
      
      return (
        <option key={personality.id} value={personality.id.toString()}>
          {displayText}
        </option>
      );
    });
  }

  renderLanguageOptions() {
    const { npcLanguages } = this.state;
    const currentLanguage = Localization.strings.getLanguage();
    
    if (!npcLanguages || npcLanguages.length === 0) {
      return (
        <option value="" disabled>No languages available</option>
      );
    }
    
    return npcLanguages.map(language => {
      const name = currentLanguage === "en" ? language.name_en : language.name_zh;
      const displayText = `${name}${language.code ? ` (${language.code})` : ''}`;
      
      return (
        <option key={language.id} value={language.id.toString()}>
          {displayText}
        </option>
      );
    });
  }

  renderGitHubStyleAvatarSelector() {
    const { npcAvatars } = this.state;
    const npcData = this.state.contentPayload || {};
    const selectedAvatarId = npcData.avatar_id;
    
    if (!npcAvatars || npcAvatars.length === 0) {
      return (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          border: '1px solid #d1d9e0',
          borderRadius: '6px',
          backgroundColor: '#f6f8fa',
          color: '#656d76'
        }}>
          <div style={{fontSize: '24px', marginBottom: '8px'}}>👤</div>
          <div>No avatars available</div>
        </div>
      );
    }
    
    return (
      <div style={{
        border: '1px solid #d1d9e0',
        borderRadius: '6px',
        padding: '16px',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: '12px',
          justifyItems: 'center'
        }}>
          {npcAvatars.map(avatar => {
            const avatarImageUrl = Config.API_PATH() + "/" + avatar.url;
            const isSelected = selectedAvatarId === avatar.id || String(selectedAvatarId) === String(avatar.id);
            
            return (
              <div
                key={avatar.id}
                onClick={() => {
                  const event = { target: { value: avatar.id } };
                  this.npcFieldChanged('avatar_id')(event);
                }}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '8px',
                  borderRadius: '6px',
                  border: isSelected ? '2px solid #0969da' : '2px solid transparent',
                  backgroundColor: isSelected ? '#ddf4ff' : 'transparent',
                  transition: 'all 0.2s ease',
                  minWidth: '80px'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#f6f8fa';
                    e.currentTarget.style.border = '2px solid #d1d9e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.border = '2px solid transparent';
                  }
                }}
              >
                <div style={{
                  position: 'relative',
                  width: '64px',
                  height: '64px',
                  borderRadius: '4px',//borderRadius: '50%',
                  overflow: 'hidden',
                  border: '1px solid #d1d9e0',
                  backgroundColor: '#f6f8fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}>
                  <img 
                    src={avatarImageUrl}
                    alt={`Avatar ${avatar.id}`}
                    style={{
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain',
                      objectPosition: 'center',
                      padding: 5
                      
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = `
                        <div style="color: #656d76; font-size: 28px;">👤</div>
                      `;
                    }}
                  />
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      bottom: '-2px',
                      right: '-2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#0969da',
                      borderRadius: '50%',
                      border: '2px solid #ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: '#ffffff',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
                
                {/* <div style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#24292f',
                  textAlign: 'center',
                  lineHeight: '1.2',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {avatar.remark || `Avatar ${avatar.id}`}
                </div>
                
                <div style={{
                  fontSize: '10px',
                  color: '#656d76',
                  marginTop: '2px',
                  fontWeight: '400'
                }}>
                  #{avatar.id}
                </div> */}
              </div>
            );
          })}
        </div>
        
        {/* {selectedAvatarId && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f6f8fa',
            borderRadius: '6px',
            border: '1px solid #d1d9e0'
          }}>
            <div style={{
              fontSize: '12px',
              color: '#656d76',
              marginBottom: '4px'
            }}>
              Selected Avatar:
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#24292f'
            }}>
              {(() => {
                const selected = npcAvatars.find(a => a.id.toString() === selectedAvatarId);
                return selected ? (selected.remark || `Avatar #${selected.id}`) : 'Unknown';
              })()}
            </div>
          </div>
        )} */}
      </div>
    );
  }

  getSelectedAvatarPreview() {
    const { npcAvatars } = this.state;
    const npcData = this.state.contentPayload || {};
    const avatarId = npcData.avatar_id;
    
    if (!avatarId || !npcAvatars.length) return null;
    
    const selectedAvatar = npcAvatars.find(avatar => avatar.id.toString() === avatarId);
    if (!selectedAvatar) return null;
    
    const avatarImageUrl = Config.API_PATH() + "/" + selectedAvatar.url;
    
    return (
      <div style={{
        padding: "16px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #e9ecef",
        textAlign: "center",
        marginBottom: "16px"
      }}>
        <div style={{
          display: "inline-block",
          position: "relative",
          marginBottom: "12px"
        }}>
          <img 
            src={avatarImageUrl}
            alt={`Selected Avatar ${selectedAvatar.id}`}
            style={{
              width: 96, 
              height: 96, 
              borderRadius: "12px", 
              border: "3px solid #fff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              objectFit: 'contain',
              objectPosition: 'center',
              padding: 5,
              backgroundColor: "#fff"
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = `
                <div style="
                  width: 96px; 
                  height: 96px; 
                  borderRadius: 12px; 
                  border: 3px solid #fff;
                  backgroundColor: #e9ecef;
                  display: flex;
                  alignItems: center;
                  justifyContent: center;
                  fontSize: 36px;
                  color: #6c757d;
                ">👤</div>
              `;
            }}
          />
          <div style={{
            position: "absolute",
            bottom: "-6px",
            right: "-6px",
            width: "24px",
            height: "24px",
            backgroundColor: "#28a745",
            borderRadius: "50%",
            border: "2px solid #fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            color: "#fff",
            fontWeight: "bold"
          }}>
            ✓
          </div>
        </div>
        
        <div style={{
          fontWeight: "600",
          fontSize: "14px",
          color: "#333",
          marginBottom: "4px"
        }}>
          {selectedAvatar.remark || `Avatar #${selectedAvatar.id}`}
        </div>
        
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px"
        }}>
          <span style={{
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            padding: "3px 8px",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: "500"
          }}>
            ID: #{selectedAvatar.id}
          </span>
          {selectedAvatar.kind === 1 && (
            <span style={{
              backgroundColor: "#e8f5e8",
              color: "#2e7d32",
              padding: "3px 8px",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: "500"
            }}>
              Image Type
            </span>
          )}
        </div>
        
        <div style={{
          fontSize: "12px",
          color: "#6c757d",
          fontStyle: "italic"
        }}>
          Selected Avatar
        </div>
      </div>
    );
  }

  // Per-locale NPC quota indicator (e.g. "1/5"), shown on the right of the
  // content-title / score header row. Supplied by CreateContentDialog after the
  // quota check passes; absent when editing or when no limit is configured.
  renderNpcQuotaBadge() {
    // The NPC quota badge is only relevant to NPC content (kind = 15); hide it for every
    // other content type.
    const currentKind = this.state.currentContent && this.state.currentContent.kind != null
      ? parseInt(this.state.currentContent.kind)
      : (this.props.contentType != null ? parseInt(this.props.contentType) : undefined);
    if (currentKind !== Config.HOTSPOT_CONTENT_NPC) {
      return null;
    }
    const npcQuota = this.props.npcQuota;
    if (!npcQuota || !(Number(npcQuota.limit) > 0)) {
      return null;
    }
    const used = npcQuota.used;
    const limit = npcQuota.limit;
    return (
      <Box
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          whiteSpace: 'nowrap',
          paddingRight: 8,
        }}
      >
        <Text size="sm" style={{ fontWeight: 600, color: BRAND_PRIMARY }}>
          {Localization.strings.NPC_QUOTA_LABEL}
        </Text>
        <Tooltip
          label={Localization.strings.formatString(Localization.strings.NPC_QUOTA_TOOLTIP, used, limit)}
          withArrow
          position="top"
        >
          <Box
            component="span"
            style={{
              fontSize: '16px',
              fontWeight: 700,
              color: BRAND_PRIMARY,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              border: '1px solid',
              borderColor: BRAND_PRIMARY,
              borderRadius: '999px',
              padding: '2px 12px',
              lineHeight: 1.6,
              cursor: 'help',
            }}
          >
            {used}/{limit}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  render() {
    let preview0 = (this.state.contentPayload.picture0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture0.tn_path : "");
    let path = (this.state.contentPayload.picture0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.picture0.path : "");
    if (path == "") {
      path = (this.state.contentPayload.sound0 != null? Config.UPLOAD_PATH() + this.state.contentPayload.sound0.path : "");
    }
    let file_ID = (this.state.contentPayload.picture0 != null? this.state.contentPayload.picture0.file_id : "");
    if (file_ID == "") {
      file_ID = (this.state.contentPayload.sound0 != null? this.state.contentPayload.sound0.file_id : "");
    }
    return (
      <div style={{ marginTop: '20px' }}> {/* Add margin here */}
        <If condition={this.isThreeDModelKind()}>
          <Then>
            <Grid gutter="md" align="center">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group align='center' gap="md" wrap="nowrap">
                  <div style={{padding:"0 0 0 8px", whiteSpace:'nowrap'}}>
                    {Localization.strings.CONTENT_NAME}
                  </div>
                  <TextInput
                    style={{padding:"0 10px 0 10px", flex:1}}
                    size="sm"
                    value={this.state.contentName}
                    onChange={this.contentNameChanged}
                    maxLength={Config.MAX_TEXT_LEN().NAME}
                  />
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group align='center' gap="md" wrap="nowrap">
                  <div style={{padding:"0 0 0 8px", whiteSpace:'nowrap'}}>
                    {Localization.strings.DESCRIPTION_TEXT}
                  </div>
                  <TextInput
                    style={{padding:"0 10px 0 10px", flex:1}}
                    size="sm"
                    value={this.state.contentText}
                    onChange={this.contentTextChanged}
                    maxLength={Config.MAX_TEXT_LEN().CONTENT}
                  />
                </Group>
              </Grid.Col>
            </Grid>
          </Then>
          <Else>
            <Group align='baseline' gap="md">
              <div style={{padding:"0 0 0 8px"}}>
                {Localization.strings.CONTENT_NAME}
              </div>
              <TextInput
                style={{padding:"0 10px 0 10px"}}
                size="sm"
                value={this.state.contentName}
                onChange={this.contentNameChanged}
                maxLength={Config.MAX_TEXT_LEN().NAME}
              />
              {this.shouldShowScoreSelector() && (
                <>
                  <div style={{padding:"0 10px", display:'flex', alignItems:'center'}}>
                    <Tooltip label={Localization.strings.MAX_SCORE_HINT} withArrow position="top">
                      <span style={{ display: 'inline-flex' }}>
                        <IconHelp
                          size={18}
                          style={{
                            marginRight: 6,
                            color: '#495057',
                            cursor: 'help',
                          }}
                        />
                      </span>
                    </Tooltip>
                    {Localization.strings.MAX_SCORE}
                  </div>
                  <select
                    value={this.state.scoreMaxUi}
                    onChange={(e) => this.setState({ scoreMaxUi: Number(e.target.value) })}
                    style={{
                      width: '120px',
                      height: '40px',
                      padding: '8px 12px',
                      margin: '8px 10px',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none',
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1976d2';
                      e.target.style.borderWidth = '2px';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                      e.target.style.borderWidth = '1px';
                    }}
                  >
                    <option value={-1}>{Localization.strings.NOT_GRADED}</option>
                    {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </>
              )}
              {this.renderNpcQuotaBadge()}
            </Group>
          </Else>
        </If>

        <If condition={this.state.currentContent != null}>
          <Then>
            <Grid gutter="md" justify="center" style={{paddingTop:'10px'}}>
              <Switch condition={parseInt(this.state.currentContent.kind)}>
                <Case value={(Config.HOTSPOT_CONTENT_SIMPLE_DESCRIPTION)}>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.imagePicker(Localization.strings.DESCRIPTION_IMAGE, "", "jpg,jpeg,png", "image", path, imageSizeLimit, preview0, "picture0", file_ID)}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.contentText(Localization.strings.DESCRIPTION_TEXT)}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_VOICE_OVER)}>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.audioPicker(Localization.strings.DESCRIPTION_AUDIO, "", "mp3", "audio", path, audioSizeLimit, "", "sound0", file_ID, this.voiceOverTranscription())}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.contentText(Localization.strings.DESCRIPTION_TEXT)}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_MOVIECLIP)}>
                  {/* Weighted 7/5 rather than the usual even split: this column
                      holds a 16:9 video preview and the description opposite it
                      is a single short field, so an even split wasted width on
                      empty space and squeezed the player. */}
                  <Grid.Col span={{ base: 12, sm: 7 }}>
                  {this.movieClipEditor(Localization.strings.MOVIECLIP_SOURCE)}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 5 }}>
                    {this.contentText(Localization.strings.DESCRIPTION_TEXT)}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_MC_OF_TEXTS)}>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.imagePicker(Localization.strings.DESCRIPTION_IMAGE, "", "jpg,jpeg,png", "image", path, imageSizeLimit, preview0, "picture0", file_ID)}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.mcText()}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_MC_OF_PICTURES)}>
                  <Grid.Col span={12}>
                    {this.contentText(Localization.strings.DESCRIPTION_QUESTION)}
                  </Grid.Col>
                  {this.mcPic()}
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI)}>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.imagePicker(Localization.strings.DESCRIPTION_IMAGE, "", "jpg,jpeg,png", "image", path, imageSizeLimit, preview0, "picture0", file_ID)}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.mcTextMulti()}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI)}>
                  <Grid.Col span={12}>
                    {this.contentText(Localization.strings.DESCRIPTION_QUESTION)}
                  </Grid.Col>
                  {this.mcPicMulti()}
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_QUESTION)}>
                  <Grid.Col span={12}>
                    {this.contentText(Localization.strings.DESCRIPTION_QUESTION)}
                    {this.markingScheme()}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK)}>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.imagePicker(Localization.strings.DESCRIPTION_IMAGE, "", "jpg,jpeg,png", "image", path, imageSizeLimit, preview0, "picture0", file_ID)}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    {this.fillInTheBlank()}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_FILL_IN_MIND_MAP)}>
                  <Grid.Col span={12}>
                    <ContentMindMap root={this.props.root} parent={this} />
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_TAKE_PHOTO)}>
                  <Grid.Col span={12}>
                    {this.contentText(Localization.strings.DESCRIPTION_TAKE_PHOTO)}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_TABLE_AND_CELL)}>
                  <Grid.Col span={12}>
                    {this.customTable()}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_TAKE_RECORD)}>
                  <Grid.Col span={12}>
                    {this.contentText(Localization.strings.DESCRIPTION_TAKE_RECORD)}
                    {this.markingScheme()}
                  </Grid.Col>
                </Case>
                {/* Backward compatibility: render NPC form for legacy kind=14 (number) and "14" (string) */}
                <Case value={(14)}>
                  <Grid.Col span={12}>
                    {this.npcForm()}
                  </Grid.Col>
                </Case>
                <Case value={("14")}>
                  <Grid.Col span={12}>
                    {this.npcForm()}
                  </Grid.Col>
                </Case>
                {/* Current NPC kind=15 (number) and "15" (string) */}
                <Case value={(Config.HOTSPOT_CONTENT_NPC)}>
                  <Grid.Col span={12}>
                    {this.npcForm()}
                  </Grid.Col>
                </Case>
                <Case value={("15")}>
                  <Grid.Col span={12}>
                    {this.npcForm()}
                  </Grid.Col>
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_3D_MODEL)}>
                  {/* The description lives in the title row above for this
                      kind — see isThreeDModelKind(). */}
                  {(() => {
                    const p = this.state.contentPayload || {};
                    const img1 = p.img_url_1;
                    const img4 = p.img_url_4;
                    const uploadPath = Config.UPLOAD_PATH();
                    const leftPath = img1 && img1.path ? (uploadPath + img1.path) : "";
                    const leftPreview = img1 && img1.tn_path ? (uploadPath + img1.tn_path) : "";
                    const leftFileId = img1 && img1.file_id ? img1.file_id : "";
                    const rightPreview = img4 && (img4.tn_path || img4.path) ? (uploadPath + (img4.tn_path || img4.path)) : "";
                    // While a generation is running the picker goes away —
                    // replacing the image would be a confusing implicit cancel,
                    // and the progress card shows that photo anyway. The
                    // settings keep their place on the right, disabled: they
                    // cannot affect a job already on the GPU (params apply to
                    // the NEXT run), but they are the only record of what this
                    // model is being built with, and a teacher watching a
                    // 10-minute progress bar wants to see it.
                    const job = this.state.gen3dJob;
                    const jobActive = Boolean(
                      (job && job.phase !== PHASE.FAILED && job.phase !== PHASE.DONE && job.phase !== PHASE.CANCELLED) ||
                      (!job && this.state.isGenerating3DModel && !this.state.is3DModelReady)
                    );
                    if (jobActive) {
                      return [
                        <Grid.Col key="progress" span={{ base: 12, sm: 6 }}>
                          {this.threeDModelPreview(rightPreview)}
                        </Grid.Col>,
                        <Grid.Col key="params" span={{ base: 12, sm: 6 }}>
                          {this.threeDGenParams({ disabled: true, showStart: false })}
                        </Grid.Col>
                      ];
                    }
                    // A finished model IS the component's content: only the
                    // viewer card shows. The picker and params would suggest
                    // a finished model can be casually replaced — the 刪除
                    // button on the viewer is the one explicit way back to
                    // the pristine upload state.
                    if (p.img_url_3 && p.img_url_3.path) {
                      return (
                        <Grid.Col key="viewer" span={12}>
                          {this.threeDModelPreview(rightPreview)}
                        </Grid.Col>
                      );
                    }
                    // The picker title carries the call to action; the (i)
                    // explains the whole flow to first-time users.
                    const pickerTitle = (
                      <Group gap={6} wrap="nowrap">
                        <span>{Localization.strings.UPLOAD_IMAGE_TO_GENERATE_3D || 'Upload an image to generate the 3D model'}</span>
                        {this.threeDInfoIcon(Localization.strings.GEN3D_UPLOAD_INFO || 'Best results: one single object, sharp and well-lit, on a clean background. PNG with transparent background works best. After uploading, set the options on the right and press "Generate 3D Model".')}
                      </Group>
                    );
                    return [
                      <Grid.Col key="left" span={{ base: 12, sm: 6 }}>
                        {this.imagePicker(pickerTitle, "", "jpg,jpeg,png", "image", leftPath, imageSizeLimit, leftPreview, "img_url_1", leftFileId)}
                      </Grid.Col>,
                      <Grid.Col key="right" span={{ base: 12, sm: 6 }}>
                        {this.threeDGenParams()}
                        {this.threeDModelPreview(rightPreview)}
                      </Grid.Col>
                    ];
                  })()}
                </Case>
                <Case value={(Config.HOTSPOT_CONTENT_DRAWING_TASK)}>
                  <Grid.Col span={12}>
                    {this.contentText(Localization.strings.DESCRIPTION_DRAWING_TASK)}
                  </Grid.Col>
                </Case>
              </Switch>
            </Grid>
          </Then>
        </If>
      </div>
    )
  }
}

export default ContentDetailView;
